/**
 * Exercise Database - Centralized, type-safe exercise definitions
 * 
 * Future-proofed structure:
 * - Hierarchical by category (Strength, Cardio, Tactical, etc.)
 * - Rich metadata for accurate tracking and muscle recovery
 * - Fuzzy-matchable by name/aliases
 * - Extensible for new exercise types
 * 
 * NOW WITH 980+ EXERCISES! 
 * - 132 tactical/operator-focused exercises (curated, optimized)
 * - 847 additional exercises from free-exercise-db (open source)
 * - Eliminated 29 low-value/duplicate exercises (Phases 1-3)
 * 
 * Phase 1: Removed 22 duplicates with inferior metadata
 * Phase 2: Back Squat → ExerciseDB's "Barbell Squat" (better metadata)
 * Phase 3: Removed 6 low-value exercises (redundant variations)
 */

import { FREE_EXERCISE_DB } from './freeExerciseDB';
import exerciseCanonicalMap from './exerciseCanonicalMap.json';
import { EXERCISE_DESCRIPTIONS } from './exerciseDescriptions';

// =============================================================================
// IMAGE BRIDGE MAP
// =============================================================================
// Maps internal canonical exercise names to their closest free-exercise-db
// equivalent for IMAGE RESOLUTION ONLY. This does NOT change tracking or aliases.
// The merge step uses this to pull images from free-exercise-db for exercises
// that exist in our internal DB but don't have a normalized-name match.
// Format: 'Internal Canonical Name' → 'Free Exercise DB Name'
//
// EXPORTED so it can be guarded. Every entry here is a SILENT no-op when
// either side fails to resolve: the merge loop `continue`s on a missing
// internal name and skips on a missing free-db target, without logging which
// ones did nothing. 34 of 63 entries were dead this way — the merge reported
// "29 image bridges applied" and nobody noticed the other 34. See
// lib/__tests__/exercisePickerImageCoverage.test.ts.
// =============================================================================
export const IMAGE_BRIDGE_MAP: Record<string, string> = {
  // === COMPOUND LIFTS ===
  'Back Squat': 'Barbell Full Squat',
  'Front Squat': 'Front Barbell Squat',
  'Bench Press': 'Barbell Bench Press - Medium Grip',
  // (Incline/decline/close-grip bench variants removed 2026-07-16 — internal
  //  entries deleted; the names now alias straight to the free-db entries.)
  'Deadlift': 'Barbell Deadlift',
  'Romanian Deadlift': 'Romanian Deadlift from Deficit',
  'Sumo Deadlift': 'Sumo Deadlift with Bands',
  'Overhead Press': 'Standing Military Press',
  'Push Press': 'Push Press',
  'Hip Thrust': 'Barbell Hip Thrust',

  // === DUMBBELL ===
  'Dumbbell Bench Press': 'Dumbbell Bench Press with Neutral Grip',
  'Incline Dumbbell Press': 'Incline Dumbbell Bench With Palms Facing In',
  'Dumbbell Shoulder Press': 'Dumbbell Shoulder Press',
  'Arnold Press': 'Arnold Dumbbell Press',
  'Dumbbell Fly': 'Dumbbell Flyes',
  'Lateral Raise': 'Side Lateral Raise',
  'Dumbbell Curl': 'Dumbbell Bicep Curl',
  'Dumbbell Shrug': 'Dumbbell Shrug',

  // === CABLES & MACHINES ===
  // free-exercise-db has no "Face Pull"; its only "face" entries are neck
  // resistance work. Cable Rope Rear-Delt Rows is the same movement pattern
  // (rope to face, external rotation, rear delts) and is the honest match.
  'Face Pull': 'Cable Rope Rear-Delt Rows',
  // Cardio: free-db carries treadmill/rope variants, so these are real matches
  // rather than approximations.
  'Run': 'Running, Treadmill',
  'Treadmill Incline Walk': 'Walking, Treadmill',
  'Jump Rope': 'Rope Jumping',
  'Russian Twist': 'Cable Russian Twists',
  'Tricep Pushdown': 'Triceps Pushdown',
  'Cable Lateral Raise': 'Cable Seated Lateral Raise',
  'Cable Crunch': 'Cable Crunch',
  'Leg Curl': 'Lying Leg Curls',
  'Seated Ham Curl': 'Seated Leg Curl',
  'Leg Press': 'Narrow Stance Leg Press',
  'Hip Abduction': 'Thigh Abductor',
  'Hip Adduction': 'Thigh Adductor',
  'Reverse Pec Deck': 'Reverse Machine Flyes',

  // === BODYWEIGHT ===
  'Pull-Ups': 'Pullups',
  'Chin-Ups': 'Chin-Up',
  'Push-Ups': 'Pushups',

  // === LEGS & GLUTES ===
  'Bulgarian Split Squat': 'Split Squat with Dumbbells',
  'Walking Lunge': 'Bodyweight Walking Lunge',
  'Goblet Squat': 'Goblet Squat',
  'Step-Ups': 'Dumbbell Step Ups',
  'Glute Bridge Iso': 'Barbell Glute Bridge',
  'Seated Calf Raise': 'Seated Calf Raise',
  'Soleus Raise': 'Seated Calf Raise',

  // === BACK ===
  'Chest Supported Row': 'Incline Dumbbell Row',
  'T-Bar Row': 'T-Bar Row with Handle',
  'Straight-Arm Pulldown': 'Straight-Arm Pulldown',

  // === ARMS ===
  'Barbell Curl': 'Barbell Curl',
  'Hammer Curl': 'Hammer Curls',
  'Skull Crusher': 'Lying Triceps Press',
  'EZ Bar Curl': 'EZ-Bar Curl',
  'Preacher Curl': 'Preacher Curl',

  // === CORE ===
  'Plank': 'Plank',
  'Dead Bug': 'Dead Bug',
  'Pallof Press': 'Pallof Press With Rotation',
  'Hanging Knee Raise': 'Hanging Leg Raise',
  'Hanging Leg Raise': 'Hanging Leg Raise',

  // === CARRIES & CONDITIONING ===
  "Farmer's Carry": 'Farmers Walk',
  'Sled Push': 'Prowler Sprint',
  'Backward Drag': 'Prowler Sprint',
  'Med Ball Slam': 'Medicine Ball Chest Pass',
  'Burpee': 'Burpees',
  'Kettlebell Swing': 'Kettlebell Sumo Deadlift High Pull',

  // === STRETCHES & MOBILITY ===
  'Cat-Cow': 'Cat Stretch',
  'Child\'s Pose': 'Child\'s Pose',
  'Hip Flexor Stretch': 'Kneeling Hip Flexor',
  'World\'s Greatest Stretch': 'Groiners',
};


/**
 * EXERCISE ALIAS MAPPING
 * Maps our old exercise names to the canonical free-exercise-db names.
 * Used for backwards compatibility with existing programs and 1RM data.
 * 
 * Format: 'Old Name' → 'Free Exercise DB Name'
 */
// =============================================================================
// EXERCISE ALIAS MAP
// =============================================================================
// PURPOSE: Map shorthand/alternate names to our INTERNAL canonical exercise names.
// RULES:
//   1. Only alias if the exercises are TRULY the same movement
//   2. Never alias to a MORE SPECIFIC variant (e.g., don't map "Pull-Ups" to "Scapular Pull-Up")
//   3. Never alias to a DIFFERENT exercise (e.g., don't map "Hip Abduction" to "Hip Adduction")
//   4. If an exercise exists in our internal DB, it IS the canonical name — don't override it
//   5. Aliases are for shorthand/typo tolerance only (e.g., "db bench" → "Dumbbell Bench Press")
// =============================================================================
export const EXERCISE_ALIAS_MAP: Record<string, string> = {
  // Squats — internal "Back Squat" gets deduplicated against FREE_EXERCISE_DB's
  // "Barbell Full Squat", so the canonical name in ALL_EXERCISES is the latter.
  // Map all the human-friendly variants to it so findExercise() resolves cleanly.
  'Back Squat': 'Barbell Full Squat',
  'back squat': 'Barbell Squat',
  'squat': 'Barbell Squat',
  'barbell squat': 'Barbell Squat',

  // Deadlifts — internal DB has "Deadlift" and "Romanian Deadlift" as canonical
  'conventional deadlift': 'Deadlift',
  'barbell deadlift': 'Deadlift',
  'rdl': 'Romanian Deadlift',
  'barbell rdl': 'Romanian Deadlift',

  // Bench Press — internal DB has "Bench Press" as canonical
  'bench': 'Bench Press',
  'flat bench': 'Bench Press',
  'barbell bench press': 'Bench Press',

  // Incline Bench — internal DB has "Incline Bench Press" as canonical
  'incline bench': 'Incline Bench Press',
  'incline barbell bench': 'Incline Bench Press',

  // Close Grip Bench
  'close grip bench': 'Close Grip Bench Press',
  'cgbp': 'Close Grip Bench Press',

  // Dumbbell Bench — internal DB has "Dumbbell Bench Press" as canonical
  'db bench press': 'Dumbbell Bench Press',
  'db bench': 'Dumbbell Bench Press',

  // Dumbbell Fly
  'db fly': 'Dumbbell Fly',
  'chest fly': 'Dumbbell Fly',

  // Push Press
  'push-press': 'Push Press',

  // Arnold Press
  'arnold press': 'Arnold Press',

  // Lateral Raise — internal DB has "Lateral Raise" as canonical
  'side raise': 'Side Lateral Raise',
  'side lateral raise': 'Side Lateral Raise',

  // Rear Delt Fly — dumbbell rear delt fly (now has own DB entry)
  'rear delt fly': 'Rear Delt Fly',
  'reverse fly': 'Rear Delt Fly',
  // Reverse Pec Deck is its own canonical exercise (machine version)
  'rear delt machine': 'Reverse Pec Deck',

  // Band Pull-Aparts (normalize plural forms)
  'Band Pull-Aparts': 'Band Pull-Apart',
  'band pull-aparts': 'Band Pull-Apart',
  'band pull aparts': 'Band Pull-Apart',

  // Normalize singular/plural exercise name variants
  'Walking Lunges': 'Walking Lunge',
  'Air Squats': 'Air Squat',
  'air squats': 'Air Squat',
  // Bodyweight Squats handled below → Air Squat (direct mapping, no chain)
  'Box Jumps': 'Box Jump',
  'box jumps': 'Box Jump',
  'Broad Jumps': 'Broad Jump',
  'broad jumps': 'Broad Jump',
  'Chest-Supported Row': 'Chest Supported Row',
  'chest-supported row': 'Chest Supported Row',
  'Pull-Up': 'Pull-Ups',
  'Weighted Pull-Ups': 'Weighted Pull-Up',
  'weighted pull-ups': 'Weighted Pull-Up',
  'Assisted Pull-Ups': 'Assisted Pull-Up',
  'assisted pull-ups': 'Assisted Pull-Up',
  'Gravitron': 'Assisted Pull-Up',
  'gravitron': 'Assisted Pull-Up',
  'Assisted Chin-Ups': 'Assisted Chin-Up',
  'assisted chin-ups': 'Assisted Chin-Up',
  'Step-Ups (Weighted)': 'Step-Ups',

  // Barbell Row — internal DB has "Barbell Row" as canonical
  'bent over row': 'Barbell Row',
  'pendlay row': 'Barbell Row',
  'bb row': 'Barbell Row',

  // Dumbbell Row — historical "Dumbbell Row" / "DB Row" entries point at
  // the canonical free-DB One-Arm Dumbbell Row (the single-arm classic).
  // The internal "Dumbbell Row" entry was removed 2026-06-04 as a
  // duplicate; these aliases preserve session history that referenced it.
  'dumbbell row': 'One-Arm Dumbbell Row',
  'db row': 'One-Arm Dumbbell Row',
  'single arm row': 'One-Arm Dumbbell Row',
  'one arm row': 'One-Arm Dumbbell Row',

  // Tactical Exercises
  'tire flips': 'Tire Flip',
  'tire flip': 'Tire Flip',
  'battle ropes': 'Battle Ropes',
  'med ball slams': 'Med Ball Slam',
  'medicine ball slams': 'Med Ball Slam',
  'ball slams': 'Med Ball Slam',

  // Cardio Equipment
  'rowing': 'Rowing, Stationary',
  'erg': 'Rowing, Stationary',
  'c2 row': 'Rowing, Stationary',
  'concept2': 'Rowing, Stationary',
  'rower': 'Rowing, Stationary',

  // Stretches — only alias genuine shorthand
  'quad stretch': 'Quad Stretch',
  'calf stretch': 'Calf Stretch',
  'tricep stretch': 'Triceps Stretch',
  'triceps stretch': 'Triceps Stretch',
  'worlds greatest stretch': 'World\'s Greatest Stretch',
  'wgs': 'World\'s Greatest Stretch',

  // Leg exercises — internal DB has canonical entries
  'leg extension': 'Leg Extension',
  'hamstring curl': 'Seated Ham Curl',
  'seated hamstring curl': 'Seated Ham Curl',
  'hip thrust': 'Hip Thrust',
  'back extension': 'Back Extension',
  'hyperextension': 'Back Extension',
  'walking lunges': 'Walking Lunge',
  'walking lunge': 'Walking Lunge',

  // Upper body — internal DB has canonical entries for all of these
  'chest supported row': 'Chest Supported Row',
  'lat pulldown': 'Lat Pulldown',
  'cable lateral raise': 'Cable Lateral Raise',
  'machine chest press': 'Machine Chest Press',
  'landmine press': 'Landmine Press',
  'cable crunch': 'Cable Crunch',
  'weighted pull-up': 'Weighted Pull-Up',
  'weighted pullup': 'Weighted Pull-Up',
  'pull-ups': 'Pull-Ups',
  'pull-up': 'Pull-Ups',
  'pullups': 'Pull-Ups',
  'chin-up': 'Chin-Ups',
  'chin up': 'Chin-Ups',
  'Chin-Up': 'Chin-Ups',
  'hanging knee raise': 'Hanging Knee Raise',
  'hanging leg raise': 'Hanging Leg Raise',
  'db shrug': 'Dumbbell Shrug',
  'dumbbell shrug': 'Dumbbell Shrug',
  'hip abduction': 'Hip Abduction',
  '1-arm cable row': '1-Arm Cable Row',
  'one arm cable row': '1-Arm Cable Row',

  // Carries & sled — internal DB has canonical entries
  'Farmer Carry': "Farmer's Carry",
  'farmer carry': "Farmer's Walk",
  'farmer walk': "Farmer's Walk",
  'farmers carry': "Farmer's Walk",
  'sled push': 'Sled Push',
  'backward drag': 'Backward Drag',
  'backward sled drag': 'Backward Drag',
  'sled drag': 'Backward Drag',

  // Core — internal DB has canonical entries
  'pallof press': 'Pallof Press',
  'pallof': 'Pallof Press',
  'dead bug': 'Dead Bug',

  // Prehab / mobility
  'band external rotation': 'Band External Rotation',
  'external rotation': 'Band External Rotation',
  'box squat': 'Box Squat',
  'straight-arm pulldown': 'Straight-Arm Pulldown',
  'straight arm pulldown': 'Straight-Arm Pulldown',
  'seated calf raise': 'Seated Calf Raise',
  'leg press calf raise': 'Leg Press Calf Raise',
  'calf press': 'Leg Press Calf Raise',
  'med ball scoop toss': 'Med Ball Scoop Toss',
  'med ball slam': 'Med Ball Slam',

  // Incline DB Press — exists in internal DB as canonical
  'incline db press': 'Incline Dumbbell Press',
  'incline dumbbell press': 'Incline Dumbbell Press',
  'Incline Dumbbell Bench Press': 'Incline Dumbbell Press',
  'incline dumbbell bench press': 'Incline Dumbbell Press',

  // Bulgarian Split Squat — exists in internal DB as canonical (NOT same as generic Split Squats)
  'bulgarian split squat': 'Bulgarian Split Squat',
  'Bulgarian Split Squats': 'Bulgarian Split Squat',
  'bss': 'Bulgarian Split Squat',

  // Programs use these variant names
  'Barbell Row (Pendlay)': 'Barbell Row',
  'Bent-Over Row': 'Barbell Row',
  'bent-over row': 'Barbell Row',
  'Strict Pull-Ups': 'Pull-Ups',
  'strict pull-ups': 'Pull-Ups',
  'Scap Pull-Ups': 'Scapular Pull-Ups',
  'scap pull-ups': 'Scapular Pull-Ups',
  'Overhead Press (DB)': 'Dumbbell Shoulder Press',
  'Plank Variations': 'Plank',
  'Plank Hold': 'Plank',
  'Bodyweight Squat': 'Air Squat',
  'bodyweight squat': 'Air Squat',
  'Bodyweight Squats': 'Air Squat',
  'bodyweight squats': 'Air Squat',

  // Distance-based cardio (programs use specific distances, all track as same exercise type)
  '1 Mile Run': 'Run',
  '1.5 Mile Run': 'Run',
  '2 Mile Run': 'Run',
  '1 mile run': 'Run',
  '1.5 mile run': 'Run',
  '2 mile run': 'Run',

  // Hip Thrust variants
  'hip thrust ramp': 'Hip Thrust',
  'Hip Thrust Ramp': 'Hip Thrust',

  // Tricep variants
  'tricep rope': 'Rope Tricep Pushdown',
  'Tricep Rope': 'Rope Tricep Pushdown',
  'rope pushdown': 'Rope Tricep Pushdown',

  // Plural/variant normalization for program exercises
  '30/60s Sprints': '30/60s Sprint',
  'Burpees': 'Burpee',
  'Ruck/Walk': 'Ruck',
  'ruck/walk': 'Ruck',
  'Ruck March': 'Ruck',
  'ruck march': 'Ruck',
  'Incline Walk': 'Treadmill Incline Walk',
  'incline walk': 'Treadmill Incline Walk',
  'Cat-Cow Stretch': 'Cat-Cow',
  'cat-cow stretch': 'Cat-Cow',
  'Band Face Pulls': 'Face Pull',
  'band face pulls': 'Face Pull',
  'Chest Stretch': 'Chest Doorway Stretch',
  'chest stretch': 'Chest Doorway Stretch',
  'Zone 2 Cardio': 'Easy Cardio',
  'zone 2 cardio': 'Easy Cardio',
};

/**
 * Resolve an exercise name to its canonical free-exercise-db name.
 *
 * Follows the alias chain to its terminal: the JSON canonical map
 * (exerciseCanonicalMap.json) is merged into the built-in EXERCISE_ALIAS_MAP,
 * and the two layers sometimes point through an intermediate (e.g. built-in
 * `'landmine press' → 'Landmine Press'`, JSON `'Landmine Press' → 'Landmine
 * Linear Jammer'`). Single-level resolution left those half-resolved, splitting
 * history by which spelling was logged. Cycle-guarded (seen-set + iteration cap)
 * so a bad A↔B pair can't loop — but a genuine two-way cycle still resolves
 * inconsistently, which the aliasMapIntegrity test forbids.
 */
export function resolveExerciseName(name: string): string {
  let current = name;
  const seen = new Set<string>([name.toLowerCase()]);
  for (let i = 0; i < 8; i++) {
    const next = EXERCISE_ALIAS_MAP[current] || EXERCISE_ALIAS_MAP[current.toLowerCase()];
    if (!next || next === current || seen.has(next.toLowerCase())) break;
    seen.add(next.toLowerCase());
    current = next;
  }
  return current;
}

// Fold the curated canonicalization aliases (reviewed in exerciseCanonicalMap.json)
// into the alias map so resolveExerciseName + normalizeExerciseMatchKey collapse
// logged spelling variants of the same lift (Pullups→Pull-Up, etc.). Single
// source of truth: edit the JSON, not a second copy.
Object.assign(EXERCISE_ALIAS_MAP, (exerciseCanonicalMap as { aliases?: Record<string, string> }).aliases || {});

// Block labels / circuit headers that get logged as if they were exercises
// ("Superset D", "Arms Superset"). They carry no real load and pollute history
// + analytics, so the save path strips them and history readers ignore them.
const NON_EXERCISE_LABELS = new Set<string>(
  ((exerciseCanonicalMap as { strip?: string[] }).strip || []).map(s => s.toLowerCase().trim())
);
export function isNonExerciseLabel(name: string | undefined | null): boolean {
  if (!name) return true;
  const n = name.toLowerCase().trim();
  if (NON_EXERCISE_LABELS.has(n)) return true;
  // Defensive catch for un-listed superset/circuit block headers.
  return /^superset\b/.test(n) || /\bsuperset$/.test(n) || /^(circuit|amrap|emom)\b/.test(n);
}

// Token synonyms collapsed during match-key normalization. Conservative — only
// equivalences that NEVER change the movement (abbreviations + tricep/triceps).
const EXERCISE_TOKEN_SYNONYMS: Record<string, string> = {
  db: 'dumbbell', bb: 'barbell', kb: 'kettlebell',
  tricep: 'triceps', bicep: 'biceps',
  pressdown: 'pushdown', pressdowns: 'pushdown', pushdowns: 'pushdown',
};
// Words that legitimately end in "s" and must NOT be de-pluralized.
const EXERCISE_PLURAL_KEEP = new Set([
  'press', 'triceps', 'biceps', 'lats', 'abs', 'glutes', 'hamstrings', 'calves',
]);
function depluralizeToken(w: string): string {
  if (EXERCISE_PLURAL_KEEP.has(w)) return w;
  if (w === 'flyes' || w === 'flies') return 'fly';
  if (w.endsWith('ies') && w.length > 4) return w.slice(0, -3) + 'y';
  if (w.endsWith('s') && w.length > 3) return w.slice(0, -1);
  return w;
}

/**
 * Build a variant-tolerant MATCH KEY for grouping logged history to a target
 * lift. Real session logs accumulate spelling variants of the SAME exercise —
 * "Incline DB Press" vs "Incline Dumbbell Press", "Chest-Supported Row" vs
 * "Chest Supported Row", "Barbell Curls" vs "Barbell Curl", "Tricep" vs
 * "Triceps". Without collapsing these, the recommendation engine starves each
 * spelling of history and cold-starts. This normalizes abbreviations, hyphens,
 * and plurals, but preserves movement-defining tokens (incline/flat, barbell/
 * dumbbell, seated/standing all stay distinct), so different lifts never merge.
 */
export function normalizeExerciseMatchKey(name: string): string {
  return resolveExerciseName(name)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(w => depluralizeToken(EXERCISE_TOKEN_SYNONYMS[w] || w))
    .join(' ')
    .trim();
}

/**
 * Machine/band ASSISTED movements (assisted pull-up/chin-up, Gravitron).
 * Their logged "weight" is the ASSISTANCE (counterbalance) — HIGHER weight
 * means MORE help = EASIER, the inverse of normal load. Consumers use this
 * to avoid inverted progression / PRs / e1RM (a bigger number is worse, not
 * better). Checks the canonical name so aliases (banded pull-up, gravitron)
 * resolve correctly.
 */
export function isAssistedExercise(name: string): boolean {
  const n = resolveExerciseName(name || '').toLowerCase();
  return /\bassisted\b/.test(n) || /gravitron/.test(n);
}

export type ExerciseCategory =
  | 'strength'           // Compound lifts, accessories
  | 'power'              // Olympic lifts, plyometrics
  | 'tactical'           // Agility, drills, combatives
  | 'cardio'             // Running, rowing, biking
  | 'ruck'               // Loaded marches specifically (Ruck, Ruck Intervals)
  | 'carry'              // Loaded carries — farmer/suitcase/yoke/sled walks (uses CarryInputRow)
  | 'swim'               // Swimming variations
  | 'circuit'            // Circuit training, AMRAPs, EMOMs
  | 'mobility'           // Stretching, yoga, flow
  | 'core'               // Planks, anti-rotation, hollow holds (NOT carries — those are 'carry')
  | 'calisthenics'       // Advanced bodyweight progressions
  | 'additionalStrength'; // Additional strength exercises

export type TrackingType =
  | 'weight-reps'      // Standard: weight, sets, reps, RPE
  | 'bodyweight-reps'  // Bodyweight: sets, reps, RPE (optional weight for dips/pull-ups)
  | 'time'             // Timed holds: duration, RPE
  | 'distance-time'    // Cardio: distance, time, RPE (continuous)
  | 'ruck'             // Ruck: weight, distance, time, RPE (continuous)
  | 'carry'            // Loaded carries: weight, distance, RPE (per session or per side)
  | 'circuit'          // Circuit: rounds, time, RPE
  | 'amrap'            // AMRAP: rounds, reps, time
  | 'emom'             // EMOM: minutes, RPE
  | 'swim-sets'        // Pool sets: distance per set, time per set, rest, RPE (per set)
  | 'interval';        // HIIT intervals: distance per interval, overall RPE (e.g., 30/60s sprints)

// ============================================================================
// AUDITED OVERRIDES — applied after dedup/merge in deduplicateAndMergeExercises
// ============================================================================
// Generated 2026-06-04 from a per-exercise human audit of the merged DB.
// Used to pin unilateral classification and tracking type for entries where
// the runtime heuristic gets it wrong — almost all are free-DB sourced
// entries where free-exercise-db has no concept of these distinctions.
//
// To re-generate: `python scripts/buildOverrideMaps.py` then paste output.

/** 143 entries — explicit unilateral classification. Key = normalized exercise id. */
const UNILATERAL_OVERRIDES: Record<string, boolean> = {
  'side_bridge': true, // audit 2026-07-15(1): side plank — per side
  // ── Audit 2026-07-08 (manual review) ───────────────────────────────
  // 'Concentration Curls' / 'Standing Concentration Curl' both resolve to the
  // internal 'concentration-curl' entry, so override that (plus the free-db
  // ids for completeness).
  'concentration-curl': true,
  'concentration_curls': true,
  'standing_concentration_curl': true,
  // ── Audit 2026-07-08 batch 2 ───────────────────────────────────────
  'air_bike': true,
  'alternate_incline_dumbbell_curl': true,
  'alternate_leg_diagonal_bound': true,
  'alternating_cable_shoulder_press': true,
  'alternating_deltoid_raise': true,
  'alternating_floor_press': true,
  'alternating_kettlebell_press': true,
  'alternating_kettlebell_row': true,
  'alternating_renegade_row': true,
  'ankle_circles': true,
  'ankle_on_the_knee': true,
  'anterior_tibialis-smr': true,
  'arnold_dumbbell_press': false, // bilateral — two implements moving together (audit fix)
  'around_the_worlds': false, // bilateral — two implements moving together (audit fix)
  'back_flyes_-_with_bands': false,
  'band_good_morning_pull_through': false,
  'bent_over_dumbbell_rear_delt_raise_with_head_on_bench': false, // bilateral — two implements moving together (audit fix)
  'bent_over_low-pulley_side_lateral': true,
  'bent_press': true,
  'bottoms-up_clean_from_the_hang_position': true,
  'cable_chest_press': false, // bilateral — two implements moving together (audit fix)
  'cable_crossover': false, // bilateral — two implements moving together (audit fix)
  'cable_incline_pushdown': false,
  'cable_internal_rotation': true,
  'cable_iron_cross': false, // bilateral — two implements moving together (audit fix)
  'cable_rear_delt_fly': false, // bilateral — two implements moving together (audit fix)
  'cable_seated_lateral_raise': true,
  'cable_shoulder_press': false, // bilateral — two implements moving together (audit fix)
  'calf_raise_on_a_dumbbell': true,
  'chain_handle_extension': false,
  'chair_leg_extended_stretch': true,
  'chair_lower_back_stretch': true,
  // ── Audit 2026-07-10 batch 3 ───────────────────────────────────────
  'chest_stretch_on_stability_ball': true,
  'cross-body_crunch': true,
  'cross_body_hammer_curl': true, // audit 2026-07-15(1)
  'cross_over_-_with_bands': true, // audit 2026-07-15: re-classified unilateral
  // ── Bilateral (override heuristic false-positives) ─────────────────
  'clean': false,
  'clean-and-jerk': false,
  'clean_and_press': false,
  'clean_deadlift': false,
  'clean_from_blocks': false,
  'clean_pull': false,
  'clean_shrug': false,
  'dumbbell_clean': false,
  'front_squat_clean_grip': false,
  'hang_clean': false,
  'hang_clean_-_below_the_knees': false,
  'hang_snatch': false,
  'hang_snatch_-_below_knees': false,
  'heaving_snatch_balance': false,
  'kettlebell-swing': false,
  'landmine-press': false,
  'lying_hamstring': true, // audit 2026-07-15(1)
  'mountain_climbers': false,
  'muscle_snatch': false,
  'open_palm_kettlebell_clean': false,
  'overhead_cable_curl': false,
  'overhead_lat': false,
  'overhead_triceps': false,
  'power_clean_from_blocks': false,
  'power_snatch_from_blocks': false,
  'sandbag-cleans': false,
  'shoulder_press_-_with_bands': false,
  'single-cone_sprint_drill': false,
  'smith_machine_hang_power_clean': false,
  'snatch': false,
  'snatch_balance': false,
  'snatch_deadlift': false,
  'snatch_from_blocks': false,
  'snatch_pull': false,
  'snatch_shrug': false,
  'split_clean': false,
  'split_snatch': false,
  'two-arm_kettlebell_clean': false,
  // ── Unilateral (pin explicit so heuristic changes don't break us) ─
  'alternating_hang_clean': true,
  'barbell_step_ups': true,
  'barbell_walking_lunge': true,
  'bent_over_two-dumbbell_row': true,
  'bent_over_two-dumbbell_row_with_palms_in': true,
  'bodyweight-step-ups': true,
  'bodyweight_walking_lunge': true,
  'crossover_reverse_lunge': true,
  'double_kettlebell_alternating_hang_clean': false, // audit 2026-07-15: re-classified bilateral
  'double_kettlebell_snatch': false, // audit 2026-07-15: re-classified bilateral
  'double_kettlebell_windmill': true,
  'dumbbell_step_ups': true,
  'kettlebell-snatch': false, // audit 2026-07-15: re-classified bilateral
  'kettlebell_dead_clean': true,
  'kettlebell_hang_clean': true,
  'kettlebell_turkish_get-up_lunge_style': true,
  'kettlebell_turkish_get-up_squat_style': true,
  'kettlebell_windmill': true,
  'lying_one-arm_lateral_raise': true,
  'lying_supine_dumbbell_curl': false, // audit 2026-07-15: re-classified bilateral
  'on-your-back_quad_stretch': true,
  'one-arm_dumbbell_row': true,
  'one-arm_flat_bench_dumbbell_flye': true,
  'one-arm_high-pulley_cable_side_bends': true,
  'one-arm_incline_lateral_raise': true,
  'one-arm_kettlebell_clean': true,
  'one-arm_kettlebell_clean_and_jerk': true,
  'one-arm_kettlebell_floor_press': true,
  'one-arm_kettlebell_jerk': true,
  'one-arm_kettlebell_military_press_to_the_side': true,
  'one-arm_kettlebell_para_press': true,
  'one-arm_kettlebell_push_press': true,
  'one-arm_kettlebell_row': true,
  'one-arm_kettlebell_snatch': true,
  'one-arm_kettlebell_split_jerk': true,
  'one-arm_kettlebell_split_snatch': true,
  'one-arm_kettlebell_swings': true,
  'one-arm_long_bar_row': true,
  'one-arm_medicine_ball_slam': true,
  'one-arm_open_palm_kettlebell_clean': true,
  'one-arm_overhead_kettlebell_squats': true,
  'one-arm_side_deadlift': true,
  'one-arm_side_laterals': true,
  'one-legged_cable_kickback': true,
  'one_arm_against_wall': true,
  'one_arm_chin-up': true,
  'one_arm_dumbbell_bench_press': true,
  'one_arm_dumbbell_preacher_curl': true,
  'one_arm_floor_press': true,
  'one_arm_lat_pulldown': true,
  'one_arm_pronated_dumbbell_triceps_extension': true,
  'one_arm_supinated_dumbbell_triceps_extension': true,
  'one_leg_barbell_squat': true,
  'pallof_press_with_rotation': true,
  'pigeon-stretch': true,
  'piriformis-smr': true,
  'pistol-squats': true,
  'push_up_to_side_plank': true,
  'quadriceps-smr': true,
  'runners_stretch': true,
  'seated_bent-over_one-arm_dumbbell_triceps_extension': true,
  'seated_one-arm_cable_pulley_rows': true,
  'seated_one-arm_dumbbell_palms-down_wrist_curl': true,
  'seated_one-arm_dumbbell_palms-up_wrist_curl': true,
  'side_leg_raises': true,
  'single-arm_cable_crossover': true,
  'single-arm_linear_jammer': true,
  'single-arm_push-up': true,
  'single-leg-bounds': true,
  'single-leg-romanian-deadlift': true,
  'single-leg_high_box_squat': true,
  'single-leg_hop_progression': false, // audit 2026-07-15: re-classified bilateral
  'single-leg_lateral_hop': true,
  'single-leg_leg_extension': true,
  'single-leg_stride_jump': true,
  'single_leg_butt_kick': true,
  'single_leg_glute_bridge': true,
  'single_leg_push-off': true,
  'smith_machine_one-arm_upright_row': true,
  'smith_machine_pistol_squat': true,
  'smith_single-leg_split_squat': true,
  'split-squat-curl-combo': true,
  'split-stance-hinge': true,
  'split_squat_with_dumbbells': true,
  'split_squats': true,
  'standing_bent-over_one-arm_dumbbell_triceps_extension': true,
  'standing_hip_circles': true,
  'standing_low-pulley_one-arm_triceps_extension': true,
  'standing_one-arm_cable_curl': true,
  'standing_one-arm_dumbbell_curl_over_incline_bench': true,
  'standing_one-arm_dumbbell_triceps_extension': true,
  'standing_palm-in_one-arm_dumbbell_press': true,
  'step-up_with_knee_raise': true,
  'step-ups': true,
  'suitcase-carry': true,
  'supine_one-arm_overhead_throw': true,
  'suspended_split_squat': true,
  'thigh_abductor': true,
  'thigh_adductor': true,
  'tricep-pushdown': false, // audit 2026-07-15: re-classified bilateral
  'two-arm_dumbbell_preacher_curl': false, // audit 2026-07-15: re-classified bilateral
  'typewriter-pull-ups': true,
  'walking-lunge': true,
  'windmills': true,
  'worlds_greatest_stretch': true,
  // -- Audit 2026-07-15 batch --
  'chest_push_from_3_point_stance': false,
  'cuban_press': false,
  'dancers_stretch': true,
  'dead_bug': true,
  'decline_dumbbell_bench_press': false,
  'decline_dumbbell_flyes': false,
  'decline_dumbbell_triceps_extension': false,
  'decline_oblique_crunch': true,
  'double_kettlebell_jerk': false,
  'double_kettlebell_push_press': false,
  'dumbbell_alternate_bicep_curl': false,
  'dumbbell_bench_press_with_neutral_grip': false,
  'dumbbell_bicep_curl': false,
  'dumbbell_flyes': false,
  'dumbbell_incline_row': false,
  'dumbbell_incline_shoulder_raise': false,
  'dumbbell_lunges': true,
  'dumbbell_lying_rear_lateral_raise': false,
  'dumbbell_raise': false,
  'dumbbell_rear_lunge': true,
  'dumbbell_side_bend': true,
  'elbow_to_knee': true,
  'fast_skipping': false,
  'front_cable_raise': true,
  'front_dumbbell_raise': true,
  'hammer_curls': false,
  'hamstring-smr': true,
  'high_cable_curls': false,
  'hip_circles_prone': true,
  'hip_extension_with_bands': true,
  'hip_flexion_with_band': true,
  'it_band_and_glute_stretch': true,
  'intermediate_groin_stretch': true,
  'intermediate_hip_flexor_and_quad_stretch': true,
  'internal_rotation_with_band': true,
  'isometric_neck_exercise_-_sides': true,
  'isometric_wipers': true,
  'kettlebell_arnold_press': true,
  'kettlebell_seated_press': true,
  'kettlebell_seesaw_press': true,
  'knee_across_the_body': true,
  'kneeling_cable_crunch_with_alternating_oblique_twists': true,
  'kneeling_hip_flexor': true,
  'landmine_180s': true,
  'lateral_bound': true,
  'latissimus_dorsi-smr': true,
  'leg-over_floor_press': true,
  'leg-up_hamstring_stretch': true,
  'lower_back-smr': true,
  'lunge_pass_through': true,
  'lunge_sprint': true,
  'cable-lateral-raise': false,
  // -- Audit 2026-07-15(1) batch --
  'glute_kickback': true,
  'leg_lift': true,
  'low_cable_crossover': false,
  'lying_crossover': true,
  'lying_glute': true,
  'middle_back_stretch': true,
  'moving_claw_series': false,
  'oblique_crunches': true,
  'oblique_crunches_-_on_the_floor': true,
  'on_your_side_quad_stretch': true,
  'one_half_locust': true,
  'one_handed_hang': true,
  'one_knee_to_chest': true,
  'peroneals-smr': true,
  'peroneals_stretch': true,
  'plate_pinch': true,
  'plate_twist': true,
  'platform_hamstring_slides': true,
  'plyo_kettlebell_pushups': true,
  'posterior_tibialis_stretch': true,
  'rear_leg_raises': true,
  'rhomboids-smr': true,
  'scissor_kick': true,
  'seated_barbell_twist': false,
  'seated_calf_stretch': true,
  'seated_floor_hamstring_stretch': true,
  'seated_hamstring_and_calf_stretch': true,
  'seated_overhead_stretch': true,
  'see-saw_press_alternating_side_press': true,
  'shotgun_row': true,
  'side-lying_floor_stretch': true,
  'side_lying_groin_stretch': true,
  'side_neck_stretch': true,
  'side_to_side_chins': true,
  'side_wrist_pull': true,
  // -- Audit 2026-07-16 batch --
  'spinal_stretch': true,
  'standing_alternating_dumbbell_press': true,
  'standing_cable_chest_press': true,
  'standing_cable_lift': true,
  'standing_cable_wood_chop': true,
  'standing_elevated_quad_stretch': true,
  'standing_gastrocnemius_calf_stretch': true,
  'standing_hamstring_and_calf_stretch': true,
  'standing_hip_flexors': true,
  'standing_low-pulley_deltoid_raise': true,
  'standing_soleus_and_achilles_stretch': true,
  'straight-arm_dumbbell_pullover': false,
  'torso_rotation': true,
  'tricep_dumbbell_kickback': true,
  'triceps_stretch': true,
  'two-arm_kettlebell_military_press': false,
  'vertical_swing': true,
  'weighted_ball_side_bend': true,
  // 'seated-dumbbell-press': false — entry deleted 2026-07-16 (merged into
  // free-db "Dumbbell Shoulder Press", which the heuristic already reads as
  // bilateral, so no re-keyed override is needed).
  'bottoms-up-kb-carry': true,
  'hamstring-stretch': true,
  'lat-stretch': true,
};

/** 5 entries — explicit tracking type. Key = normalized exercise id. */
const TRACKING_TYPE_AUDIT_OVERRIDES: Partial<Record<string, TrackingType>> = {
  /**
   * Added 2026-08-13 while fixing the sled report. Both of these are the
   * TARGET of a canonical alias whose SOURCE carried the right tracking, so
   * the alias was quietly changing what the athlete records:
   *
   *   'Hanging Knee Raise' [bodyweight-reps] -> this entry [weight-reps]
   *   'Cat Stretch'        [time]            -> 'Cat-Cow' [bodyweight-reps]
   *
   * Same convention as `dips_-_chest_version` and `front_box_jump` above:
   * keep the alias so history stays unified, and move the correct tracking
   * onto the free-DB entry it points at. A hanging knee raise is bodyweight
   * work; the parallel-bars entry inheriting 'weight-reps' from the free DB
   * put a barbell load field on it.
   */
  'knee_hip_raise_on_parallel_bars': 'bodyweight-reps',
  // 'Cat Stretch' aliases to 'Cat-Cow', so its own entry is shadowed and
  // unreachable; align it rather than leave two types for one movement.
  // Cat-Cow is prescribed by reps across the shipped programs ("1 x 8").
  'cat_stretch': 'bodyweight-reps',
  // 'Pistol Squats' aliases here (2026-07-15 audit — same movement, and the
  // free DB only carries the loaded variant). A pistol squat is bodyweight
  // work that MAY be loaded, and 'bodyweight-reps' already allows an optional
  // weight; 'weight-reps' demands one.
  'kettlebell_pistol_squat': 'bodyweight-reps',
  'barbell_ab_rollout': 'bodyweight-reps',
  'barbell_ab_rollout_-_on_knees': 'bodyweight-reps',
  'decline_push-up': 'bodyweight-reps',
  'ruck-intervals': 'ruck',
  'scapular_pull-up': 'bodyweight-reps',
  // ── Audit 2026-07-08 batch 2 ───────────────────────────────────────
  'ab_roller': 'bodyweight-reps',
  'back_flyes_-_with_bands': 'bodyweight-reps',
  'balance_board': 'time',
  'band_good_morning': 'bodyweight-reps',
  'barbell_rollout_from_bench': 'bodyweight-reps',
  'battling_ropes': 'time', // timed conditioning
  'bench_dips': 'weight-reps',
  'bench_press_-_with_bands': 'weight-reps', // banded barbell — loaded
  'bench_sprint': 'bodyweight-reps',
  'bodyweight_flyes': 'bodyweight-reps',
  'bodyweight_mid_row': 'bodyweight-reps',
  'box_skip': 'bodyweight-reps',
  'calf_raise_on_a_dumbbell': 'bodyweight-reps',
  // -- Audit 2026-07-15 batch --
  'chest_push_from_3_point_stance': 'weight-reps',
  'cross_over_-_with_bands': 'bodyweight-reps',
  'crossover_reverse_lunge': 'bodyweight-reps',
  'decline_crunch': 'weight-reps',
  'depth_jump_leap': 'bodyweight-reps',
  'dips_-_triceps_version': 'weight-reps',
  'drop_push': 'bodyweight-reps',
  'exercise_ball_crunch': 'bodyweight-reps',
  'exercise_ball_pull-in': 'bodyweight-reps',
  'external_rotation_with_band': 'bodyweight-reps',
  'floor_glute-ham_raise': 'bodyweight-reps',
  'forward_drag_with_press': 'carry',
  'front_box_jump': 'bodyweight-reps',
  'front_cone_hops_or_hurdle_hops': 'time',
  'front_leg_raises': 'bodyweight-reps',
  'groiners': 'bodyweight-reps',
  'hanging_bar_good_morning': 'bodyweight-reps',
  'heavy_bag_thrust': 'bodyweight-reps',
  'hip_circles_prone': 'bodyweight-reps',
  'hip_extension_with_bands': 'bodyweight-reps',
  'hip_flexion_with_band': 'bodyweight-reps',
  'hip_lift_with_band': 'bodyweight-reps',
  'hurdle_hops': 'time', // discrete plyometric hops, not continuous distance
  'inchworm': 'bodyweight-reps',
  'incline_push-up_depth_jump': 'bodyweight-reps',
  'internal_rotation_with_band': 'bodyweight-reps',
  'kipping_muscle_up': 'bodyweight-reps',
  'kneeling_arm_drill': 'bodyweight-reps',
  'lateral_cone_hops': 'time', // discrete plyometric hops
  'lateral_raise_-_with_bands': 'bodyweight-reps',
  'single-leg_stride_jump': 'bodyweight-reps',
  'single_leg_push-off': 'bodyweight-reps',
  'split_squats': 'bodyweight-reps',
  'suspended_split_squat': 'bodyweight-reps',
  // -- Audit 2026-07-15(1) batch --
  'linear_acceleration_wall_drill': 'bodyweight-reps',
  'linear_depth_jump': 'bodyweight-reps',
  'lower_back_curl': 'bodyweight-reps',
  'mountain_climbers': 'bodyweight-reps',
  'muscle_up': 'bodyweight-reps',
  'parallel_bar_dip': 'bodyweight-reps',
  'physioball_hip_bridge': 'bodyweight-reps',
  'pin_presses': 'weight-reps',
  'platform_hamstring_slides': 'bodyweight-reps',
  'plyo_kettlebell_pushups': 'bodyweight-reps',
  'prone_manual_hamstring': 'bodyweight-reps',
  'push-ups_with_feet_on_an_exercise_ball': 'bodyweight-reps',
  'quick_leap': 'bodyweight-reps',
  'rack_pull_with_bands': 'weight-reps', // banded barbell — loaded (Fable audit)
  'rear_leg_raises': 'bodyweight-reps',
  'rocky_pull-ups_pulldowns': 'bodyweight-reps',
  'rope_jumping': 'time',
  'scissor_kick': 'bodyweight-reps',
  'seated_band_hamstring_curl': 'bodyweight-reps',
  'shoulder_press_-_with_bands': 'bodyweight-reps',
  'side_bridge': 'time',
  'side_hop-sprint': 'time',
  'side_standing_long_jump': 'time',
  'side_to_side_box_shuffle': 'time',
  'single-cone_sprint_drill': 'time',
  'speed_band_overhead_triceps': 'weight-reps',
  // -- Audit 2026-07-16 batch --
  'squats_-_with_bands': 'weight-reps', // banded barbell — loaded
  'stride_jump_crossover': 'bodyweight-reps',
  'suspended_fallout': 'bodyweight-reps',
  'suspended_push-up': 'bodyweight-reps',
  'suspended_reverse_crunch': 'bodyweight-reps',
  'toe_touchers': 'bodyweight-reps',
  'upright_row_-_with_bands': 'bodyweight-reps',
  'weighted_sit-ups_-_with_bands': 'weight-reps', // weighted — loaded
  'spanish-squat-iso': 'time', // isometric hold
  // 'back-extension' key removed 2026-07-16 — entry deleted; "Back Extension"
  // resolves to free-db "Glute Ham Raise" which is already weight-reps.
  'jump-squats': 'weight-reps',
  // ── DB debloat 2026-07-16: tracking semantics moved from deleted internal
  //    entries onto their free-db merge targets ──
  'dips_-_chest_version': 'bodyweight-reps', // was internal 'dips' (bodyweight-reps)
  // (front_box_jump — internal 'box-jump' semantics — was already pinned to
  //  bodyweight-reps in the batch above.)
};

/** 15 entries -- explicit load basis (audit 2026-07-15). Key = normalized id. */
const LOAD_BASIS_AUDIT_OVERRIDES: Partial<Record<string, 'total' | 'per-hand'>> = {
  'dumbbell_lying_rear_lateral_raise': 'per-hand',
  'dumbbell_one-arm_shoulder_press': 'total', // audit 2026-07-16
  'dumbbell_seated_box_jump': 'total', // audit 2026-07-16
  'farmers_walk': 'per-hand', // audit 2026-07-16
  'flat_bench_cable_flyes': 'per-hand', // audit 2026-07-16
  'floor_press_with_chains': 'total', // audit 2026-07-16
  'front_squats_with_two_kettlebells': 'per-hand', // audit 2026-07-16
  'kettlebell_thruster': 'per-hand', // audit 2026-07-16
  'lying_supine_dumbbell_curl': 'per-hand', // audit 2026-07-16
  'single-arm_cable_crossover': 'total', // audit 2026-07-16
  'split_squat_with_dumbbells': 'per-hand', // audit 2026-07-16
  'two-arm_dumbbell_preacher_curl': 'per-hand', // audit 2026-07-16
  'kettlebell-snatch': 'per-hand', // audit 2026-07-16
  // -- Audit 2026-07-15(1) batch --
  'full_range-of-motion_lat_pulldown': 'per-hand', // audit 2026-07-16
  'low_cable_crossover': 'per-hand', // audit 2026-07-16
  'overhead_cable_curl': 'per-hand', // audit 2026-07-16
  'plie_dumbbell_squat': 'total', // audit 2026-07-16
  'seated_barbell_twist': 'total', // audit 2026-07-16
  'single_dumbbell_raise': 'total', // audit 2026-07-16
  // -- Audit 2026-07-16 batch --
  'straight-arm_dumbbell_pullover': 'total',
  'two-arm_kettlebell_clean': 'per-hand',
  'two-arm_kettlebell_jerk': 'per-hand',
  'two-arm_kettlebell_military_press': 'per-hand',
  'two-arm_kettlebell_row': 'per-hand',
  // re-keyed from deleted 'seated-dumbbell-press' (2026-07-16 debloat) — the
  // per-hand correction applies to the free-db merge target too:
  'dumbbell_shoulder_press': 'per-hand',
  'overhead-tricep-extension': 'total',
};

export type MuscleGroup =
  | 'chest' | 'back' | 'shoulders' | 'biceps' | 'triceps' | 'forearms'
  | 'quads' | 'hamstrings' | 'glutes' | 'calves'
  | 'core' | 'abs' | 'obliques'
  | 'full-body' | 'cardio-specific';

export interface Exercise {
  id: string;                    // Unique identifier (kebab-case)
  name: string;                  // Display name
  aliases: string[];             // Alternative names for fuzzy matching
  category: ExerciseCategory;    // Primary category
  trackingType: TrackingType;    // How to track this exercise
  primaryMuscles: MuscleGroup[]; // Main muscles worked
  secondaryMuscles: MuscleGroup[];// Supporting muscles
  equipment?: string[];          // Required equipment
  requiresOneRM: boolean;        // Does this exercise use 1RM for programming?
  variations?: string[];         // Common variations (e.g., "Incline", "Dumbbell")
  /**
   * Explicit laterality override for isUnilateralExercise. When set, it
   * wins over the name/variation regex — use it where the heuristic gets
   * it wrong (e.g. Pallof Press is per-side but has no "single" in its
   * name; Romanian Deadlift is bilateral but lists a "Single Leg"
   * variation the regex would otherwise trip on).
   */
  unilateral?: boolean;
  /**
   * How the logged/prescribed weight relates to total load, for volume math:
   *  - 'per-hand': two dumbbells, one per hand, moving together (DB bench,
   *    Arnold press, DB clean) — logged weight is per hand, so true volume is
   *    weight × reps × 2.
   *  - 'total': a single implement or bar (barbell, goblet, landmine, machine,
   *    single dumbbell) — logged weight IS the total; volume × 1.
   * Undefined → inferred by isPerHandLoad() from name/equipment. This is set in
   * the exercise audit (Load field). Note: unilateral movements always resolve
   * to total here (their doubling is handled by the per-side reps path).
   */
  loadBasis?: 'total' | 'per-hand';
  description?: string;          // Optional notes / first instruction
  tags?: string[];               // Additional searchable tags
  images?: string[];             // Image URLs (typically 2 images showing start/end positions)
  
  // Enhanced metadata (from free-exercise-db)
  force?: 'pull' | 'push' | 'static';        // Movement direction
  mechanic?: 'compound' | 'isolation';       // Movement pattern
  level?: 'beginner' | 'intermediate' | 'expert'; // Difficulty level
  instructions?: string[];                   // Full step-by-step instructions
}

/**
 * Exercise Database - Organized by category for maintainability
 * To add a new exercise: Find its category and add to the array
 */
export const EXERCISE_DATABASE: Record<ExerciseCategory, Exercise[]> = {
  
  // ============================================================================
  // STRENGTH - Compound lifts and strength accessories
  // ============================================================================
  strength: [
    // SQUAT VARIATIONS
    {
      id: 'back-squat',
      name: 'Back Squat',
      aliases: ['squat', 'barbell squat', 'bb squat'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core', 'back'],
      equipment: ['barbell', 'squat rack'],
      requiresOneRM: true,
      force: 'push',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Place the barbell on your upper traps/rear delts. Unrack, step back, and squat to parallel or below by pushing hips back and bending knees. Drive through midfoot to stand.',
      instructions: [
        'Set the barbell at shoulder height on a squat rack. Step under the bar and position it across your upper traps (high bar) or rear delts (low bar).',
        'Grip the bar firmly, unrack by straightening your legs, and take two steps back. Stand with feet shoulder-width apart, toes slightly turned out.',
        'Take a deep breath, brace your core, and descend by pushing your hips back and bending your knees. Keep your chest up and back neutral.',
        'Lower until your hip crease is at or below your knee (parallel or deeper). Do not let your knees cave inward.',
        'Drive through your midfoot to stand back up, exhaling at the top. Squeeze glutes at lockout.',
      ],
    },
    {
      id: 'front-squat',
      name: 'Front Squat',
      aliases: ['front squat', 'front rack squat'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'core'],
      secondaryMuscles: ['glutes', 'back'],
      equipment: ['barbell', 'squat rack'],
      requiresOneRM: true,
      force: 'push',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Hold the barbell in a front rack position across the front delts. Squat deeply while keeping torso upright. Emphasizes quads and core more than back squat.',
      instructions: [
        'Set the barbell at shoulder height. Step up and position the bar across your front delts, elbows high and forward in a clean grip or cross-arm grip.',
        'Unrack the bar and step back. Stand with feet shoulder-width apart, toes slightly turned out.',
        'Brace your core, keep your elbows high, and descend by pushing knees forward and hips down. Your torso should stay upright.',
        'Squat as deep as mobility allows while maintaining an upright torso. The bar should stay over your midfoot.',
        'Drive through your midfoot to stand, keeping elbows high throughout the movement.',
      ],
    },
    {
      id: 'goblet-squat',
      name: 'Goblet Squat',
      aliases: ['goblet squat', 'kb squat', 'kettlebell squat'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['core'],
      equipment: ['kettlebell'],
      requiresOneRM: false,
      force: 'push',
      mechanic: 'compound',
      level: 'beginner',
      description: 'Hold a kettlebell or dumbbell at chest height with both hands. Squat deeply between your legs. Great for learning squat mechanics.',
      instructions: [
        'Hold a kettlebell or dumbbell vertically at chest height, cupping the weight with both hands close to your chest.',
        'Stand with feet slightly wider than shoulder-width, toes turned out 15-30 degrees.',
        'Brace your core and squat down by pushing your hips back and knees out. Keep the weight close to your body and your chest tall.',
        'Descend until your elbows touch or pass your inner knees. Use your elbows to push your knees out at the bottom.',
        'Drive through your heels to stand back up. Squeeze your glutes at the top.',
      ],
    },
    // Walking Lunge — generic version (can be weighted with DB/BB/KB)
    {
      id: 'walking-lunge',
      name: 'Walking Lunge',
      aliases: ['walking lunges'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: ['dumbbells', 'barbell', 'none'],
      requiresOneRM: false,
    },
    // Walking Lunge (Bodyweight) - Override freeExerciseDB to use bodyweight tracking
    {
      id: 'bodyweight-walking-lunge',
      name: 'Bodyweight Walking Lunge',
      aliases: ['bodyweight walking lunge', 'bodyweight lunges', 'bw lunges'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: ['none'],
      requiresOneRM: false,
    },
    {
      id: 'step-ups',
      name: 'Step-Ups',
      aliases: ['step-ups', 'step ups', 'step-up', 'step up', 'step-ups (weighted)', 'weighted step-ups'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'calves'],
      equipment: ['box', 'dumbbells'],
      requiresOneRM: false,
    },
    // Bodyweight Step-Ups - for programs that don't specify weight
    {
      id: 'bodyweight-step-ups',
      name: 'Bodyweight Step-Ups',
      aliases: ['bodyweight step-ups', 'bodyweight step ups', 'bw step-ups', 'bw step ups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'calves'],
      equipment: ['box'],
      requiresOneRM: false,
    },
    {
      id: 'bulgarian-split-squat',
      name: 'Bulgarian Split Squat',
      aliases: ['bulgarian split squat', 'rear foot elevated split squat', 'split squat'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings'],
      equipment: ['dumbbells', 'bench'],
      requiresOneRM: false,
    },
    {
      id: 'split-squat-curl-combo',
      name: 'Split Squat Curl Combo',
      aliases: ['split squat curl combo', 'split squat curl', 'bulgarian split squat curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes', 'biceps'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: ['dumbbells', 'bench'],
      requiresOneRM: false,
      description: 'Compound movement combining a split squat with a bicep curl at the bottom position',
    },
    // Leg Press → mapped to "Narrow Stance Leg Press" in free-exercise-db

    // DEADLIFT VARIATIONS
    {
      id: 'deadlift',
      name: 'Deadlift',
      aliases: ['conventional deadlift', 'barbell deadlift'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'glutes', 'hamstrings'],
      secondaryMuscles: ['quads', 'core', 'forearms'],
      equipment: ['barbell'],
      requiresOneRM: true,
      force: 'pull',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Stand over a barbell with feet hip-width apart. Hinge at hips, grip the bar, brace core, and pull by driving through the floor until standing tall.',
      instructions: [
        'Stand with feet hip-width apart, barbell over midfoot. Bend at the hips and knees to grip the bar just outside your legs.',
        'Drop your hips, lift your chest, pull your shoulders back, and brace your core. Arms should be straight.',
        'Drive through your feet, extending hips and knees simultaneously. Keep the bar close to your body.',
        'Stand tall at the top with hips fully extended and shoulders back. Do not hyperextend your lower back.',
        'Lower the bar by hinging at the hips first, then bending the knees once the bar passes them. Control the descent.',
      ],
    },
    {
      id: 'romanian-deadlift',
      name: 'Romanian Deadlift',
      aliases: ['rdl', 'romanian deadlift', 'stiff leg deadlift', 'romanian dl', 'db rdl', 'dumbbell rdl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['hamstrings', 'glutes'],
      secondaryMuscles: ['back', 'core'],
      equipment: ['barbell'],
      requiresOneRM: false,
      unilateral: false, // bilateral — "Single Leg" is a separate exercise, don't infer per-side
      force: 'pull',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Start standing with the barbell. Hinge at the hips, pushing them back while keeping a slight knee bend. Lower the bar along your legs until you feel a deep hamstring stretch, then drive hips forward.',
      variations: ['Barbell', 'Dumbbell', 'Single Leg'],
      instructions: [
        'Stand with feet hip-width apart holding the barbell at hip level with an overhand grip.',
        'With a slight bend in your knees (and keep this bend fixed), hinge at the hips to lower the bar along the front of your legs.',
        'Push your hips back as the bar descends. Keep your back flat and chest up. Feel the stretch in your hamstrings.',
        'Lower until you feel a strong hamstring stretch (typically mid-shin to just below the knee). Do not round your back.',
        'Drive your hips forward to return to standing, squeezing your glutes at the top.',
      ],
    },
    {
      id: 'sumo-deadlift',
      name: 'Sumo Deadlift',
      aliases: ['sumo deadlift', 'sumo dl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['glutes', 'hamstrings', 'back'],
      secondaryMuscles: ['quads', 'core'],
      equipment: ['barbell'],
      requiresOneRM: true,
      force: 'pull',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Wide-stance deadlift with toes turned out. Grip the bar inside your legs. Emphasizes glutes and inner thighs more than conventional.',
      instructions: [
        'Stand with a very wide stance, toes turned out 30-45 degrees, barbell over midfoot.',
        'Bend at the hips and knees to grip the bar with hands inside your legs, about shoulder-width apart.',
        'Drop your hips, lift your chest, and push your knees out toward your toes. Brace your core.',
        'Drive through the floor, pushing your knees out and extending hips and knees simultaneously.',
        'Stand tall at the top. Lower by reversing the movement with control.',
      ],
    },
    {
      id: 'trap-bar-deadlift',
      name: 'Trap Bar Deadlift',
      aliases: ['trap bar deadlift', 'hex bar deadlift', 'trap bar dl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes', 'back'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: ['trap bar'],
      requiresOneRM: false,
      force: 'pull',
      mechanic: 'compound',
      level: 'beginner',
      description: 'Step inside a hex/trap bar, grip the handles at your sides, and stand up. More quad-dominant than conventional deadlift with less lower back stress.',
      instructions: [
        'Step inside the trap bar with feet hip-width apart. The handles should be at your sides.',
        'Bend at the hips and knees to grip both handles. Keep your chest up and back flat.',
        'Brace your core, drive through your feet, and stand up by extending hips and knees.',
        'Stand tall at the top with shoulders back. The weight stays centered around your body (unlike a barbell).',
        'Lower with control by hinging at the hips and bending the knees.',
      ],
    },

    // BENCH PRESS VARIATIONS
    // Incline/decline/close-grip barbell bench variants were deleted
    // 2026-07-16 as free-exercise-db duplicates — their names alias to the
    // canonical free-db entries via exerciseCanonicalMap.json. Flat "Bench
    // Press" stays internal: exerciseMatchKey.test.ts pins its match key to
    // 'bench press', so it must keep resolving to itself.
    {
      id: 'bench-press',
      name: 'Bench Press',
      aliases: ['bench', 'flat bench', 'barbell bench press', 'bb bench'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['triceps', 'shoulders'],
      equipment: ['barbell', 'flat bench'],
      requiresOneRM: true,
      force: 'push',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Lie on a flat bench, unrack the barbell with arms extended, lower to mid-chest, then press back to lockout.',
      instructions: [
        'Lie flat on the bench with your eyes under the bar. Plant your feet firmly on the floor. Grip the bar slightly wider than shoulder-width.',
        'Retract your shoulder blades and create a slight arch in your upper back. Unrack the bar with arms fully extended.',
        'Lower the bar with control to your mid-chest (nipple line), keeping elbows at roughly 45-75 degrees from your body.',
        'Touch your chest lightly without bouncing. Press the bar back up in a slight arc toward your face, ending over your shoulders.',
        'Lock out your arms at the top. Maintain shoulder blade retraction throughout the set.',
      ],
    },
    {
      id: 'dumbbell-bench-press',
      name: 'Dumbbell Bench Press',
      aliases: ['db bench press', 'db bench', 'flat dumbbell bench'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['triceps', 'shoulders'],
      equipment: ['dumbbells', 'flat bench'],
      requiresOneRM: false,
      force: 'push',
      mechanic: 'compound',
      level: 'beginner',
      description: 'Lie on a flat bench with dumbbells at chest height, press them up until arms are extended. Greater range of motion than barbell bench.',
      instructions: [
        'Sit on a flat bench with dumbbells on your thighs. Kick them up as you lie back, positioning them at chest height.',
        'Start with dumbbells at the sides of your chest, palms facing forward, elbows at about 45 degrees.',
        'Press the dumbbells up and slightly together until your arms are extended. Do not clank the dumbbells together at the top.',
        'Lower with control until you feel a stretch in your chest. The dumbbells should go slightly deeper than a barbell bench press.',
      ],
    },
    {
      id: 'incline-dumbbell-press',
      name: 'Incline Dumbbell Press',
      aliases: ['incline db press', 'incline dumbbell press', 'incline dumbbell bench'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['chest', 'shoulders'],
      secondaryMuscles: ['triceps'],
      equipment: ['dumbbells', 'incline bench'],
      requiresOneRM: false,
      force: 'push',
      mechanic: 'compound',
      level: 'beginner',
      description: 'Dumbbell press on a 30-45 degree incline. Targets upper chest with greater range of motion than barbell incline.',
      instructions: [
        'Set the bench to 30-45 degrees. Sit with dumbbells on your thighs and kick them up as you lie back.',
        'Hold the dumbbells at the sides of your upper chest with palms facing forward.',
        'Press the dumbbells up and slightly inward until arms are extended. Keep your back flat against the bench.',
        'Lower with control until you feel a stretch in your upper chest. Repeat.',
      ],
    },
    // Dumbbell Fly → mapped to "Dumbbell Flyes" in free-exercise-db

    // SHOULDER PRESS VARIATIONS
    {
      id: 'overhead-press',
      name: 'Overhead Press',
      aliases: ['ohp', 'military press', 'shoulder press', 'overhead press', 'standing press', 'strict press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps', 'core'],
      equipment: ['barbell'],
      requiresOneRM: true,
      force: 'push',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Standing barbell press from shoulders to overhead lockout. The foundational vertical pressing movement.',
      variations: ['Strict', 'Push Press', 'Seated', 'Standing'],
      instructions: [
        'Unrack the barbell at shoulder height with a grip just outside shoulder width. Stand with feet hip-width apart.',
        'Brace your core and squeeze your glutes. The bar should rest on the front of your shoulders.',
        'Press the bar straight up, moving your head slightly back to clear the bar path, then forward once the bar passes your forehead.',
        'Lock out overhead with the bar directly over your midfoot, biceps by your ears.',
        'Lower the bar with control back to your shoulders.',
      ],
    },
    {
      id: 'push-press',
      name: 'Push Press',
      aliases: ['push-press', 'push press'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps', 'quads', 'core'],
      equipment: ['barbell'],
      requiresOneRM: false,
      force: 'push',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Overhead press using leg drive to initiate the lift. Allows heavier loads than strict press for shoulder overload.',
      instructions: [
        'Start with the bar at shoulder height in a front rack position, feet hip-width apart.',
        'Dip by bending your knees 3-4 inches (quarter squat), keeping your torso vertical.',
        'Explosively drive through your legs to accelerate the bar off your shoulders, then press to lockout.',
        'Lock out overhead with arms fully extended. Lower the bar back to your shoulders with control.',
      ],
    },
    // 'seated-dumbbell-press' deleted 2026-07-16 — "Seated Dumbbell Press"
    // aliases to the free-db "Dumbbell Shoulder Press" (seated in free-db form).
    {
      id: 'dumbbell-shoulder-press',
      name: 'Dumbbell Shoulder Press',
      aliases: ['db shoulder press', 'standing db press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps'],
      equipment: ['dumbbells'],
      requiresOneRM: false,
    },
    {
      id: 'arnold-press',
      name: 'Arnold Press',
      aliases: ['arnold press', 'arnold dumbbell press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps'],
      equipment: ['dumbbells'],
      requiresOneRM: false,
    },
    {
      id: 'lateral-raise',
      name: 'Lateral Raise',
      aliases: ['lateral raise', 'side raise', 'side lateral raise'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: [],
      equipment: ['dumbbells'],
      requiresOneRM: false,
    },
    // 'front-raise' deleted 2026-07-16 — "Front Raise" aliases to free-db
    // "Front Dumbbell Raise".
    {
      id: 'dumbbell-fly',
      name: 'Dumbbell Fly',
      aliases: ['db fly', 'chest fly', 'dumbbell flyes'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['shoulders'],
      equipment: ['dumbbells', 'flat bench'],
      requiresOneRM: false,
    },
    // 'rear-delt-fly' deleted 2026-07-16 — "Rear Delt Fly" aliases to free-db
    // "Reverse Flyes".
    {
      id: 'face-pull',
      name: 'Face Pull',
      aliases: ['face pull', 'cable face pull', 'rope face pull'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders', 'back'],
      secondaryMuscles: [],
      equipment: ['cable machine', 'rope'],
      requiresOneRM: false,
    },
    {
      id: 'band-pull-apart',
      name: 'Band Pull-Apart',
      aliases: ['band pull-aparts', 'band pull aparts', 'band pull apart'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders', 'back'],
      secondaryMuscles: [],
      equipment: ['resistance band'],
      requiresOneRM: false,
      description: 'Rear delt and upper back activation with resistance band',
    },

    // BACK/ROWING VARIATIONS
    // Barbell Row → mapped to "Bent Over Barbell Row" in free-exercise-db
    {
      id: 'underhand-barbell-row',
      name: 'Underhand Barbell Row',
      aliases: ['underhand barbell row', 'reverse grip barbell row', 'supinated barbell row', 'underhand row'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['core'],
      equipment: ['barbell'],
      requiresOneRM: false,
      description: 'Barbell row with underhand (supinated) grip for increased bicep activation',
    },
    // Dumbbell Row deleted 2026-06-04 — duplicate of free-DB's "One-Arm
    // Dumbbell Row". This entry's own description ("One-arm row with knee
    // and hand supported on a bench") matches free-DB's one-arm entry,
    // NOT free-DB's "Bent Over Two-Dumbbell Row" (which is two-handed).
    // Aliases redirected via EXERCISE_ALIAS_MAP so historical user logs
    // resolve cleanly.
    // 'barbell-row' deleted 2026-07-16 — "Barbell Row" aliases to free-db
    // "Bent Over Barbell Row" (exerciseCanonicalMap.json).
    {
      id: 'chest-supported-row',
      name: 'Chest Supported Row',
      aliases: ['chest supported row', 'seal row', 'chest supported db row'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps'],
      equipment: ['dumbbells', 'incline bench'],
      requiresOneRM: false,
    },
    // 'seated-cable-row' deleted 2026-07-16 — "Seated Cable Row" aliases to
    // free-db "Seated Cable Rows".
    {
      id: 't-bar-row',
      name: 'T-Bar Row',
      aliases: ['t bar row', 't-bar row', 'landmine row'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps'],
      equipment: ['barbell', 'landmine'],
      requiresOneRM: false,
    },
    // 'lat-pulldown' deleted 2026-07-16 — "Lat Pulldown" aliases to free-db
    // "Wide-Grip Lat Pulldown" (the generic pulldown; NOT the crisscross
    // stirrup-cable "Full Range-Of-Motion Lat Pulldown", which is per-hand).
    {
      id: 'underhand-lat-pulldown',
      name: 'Underhand Lat Pulldown',
      aliases: ['underhand lat pulldown', 'reverse grip lat pulldown', 'supinated lat pulldown', 'underhand pulldown'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: [],
      equipment: ['cable machine'],
      requiresOneRM: false,
      description: 'Lat pulldown with underhand grip for increased bicep activation',
    },

    // BICEP EXERCISES
    {
      id: 'barbell-curl',
      name: 'Barbell Curl',
      aliases: ['barbell curl', 'bicep curl', 'bb curl', 'ez bar curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps'],
      secondaryMuscles: ['forearms'],
      equipment: ['barbell'],
      requiresOneRM: true,
      variations: ['Straight Bar', 'EZ Bar', 'Wide Grip', 'Close Grip'],
    },
    {
      id: 'dumbbell-curl',
      name: 'Dumbbell Curl',
      aliases: ['dumbbell curl', 'db curl', 'bicep curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps'],
      secondaryMuscles: ['forearms'],
      equipment: ['dumbbells'],
      requiresOneRM: true,
      variations: ['Alternating', 'Simultaneous', 'Hammer', 'Seated', 'Incline'],
    },
    {
      id: 'hammer-curl',
      name: 'Hammer Curl',
      aliases: ['hammer curl', 'neutral grip curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps', 'forearms'],
      secondaryMuscles: [],
      equipment: ['dumbbells'],
      requiresOneRM: true,
    },
    {
      id: 'wrist-curl',
      name: 'Wrist Curl',
      aliases: ['wrist curl', 'barbell wrist curl', 'dumbbell wrist curl', 'forearm curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['forearms'],
      secondaryMuscles: [],
      equipment: ['barbell', 'dumbbells'],
      requiresOneRM: false,
      description: 'Flexion movement for forearm development',
    },
    {
      id: 'reverse-wrist-curl',
      name: 'Reverse Wrist Curl',
      aliases: ['reverse wrist curl', 'reverse forearm curl', 'wrist extension'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['forearms'],
      secondaryMuscles: [],
      equipment: ['barbell', 'dumbbells'],
      requiresOneRM: false,
      description: 'Extension movement for forearm development',
    },
    {
      id: 'preacher-curl',
      name: 'Preacher Curl',
      aliases: ['preacher curl', 'scott curl', 'ez bar preacher curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps'],
      secondaryMuscles: [],
      equipment: ['ez bar', 'preacher bench'],
      requiresOneRM: true,
    },
    {
      id: 'incline-dumbbell-curl',
      name: 'Incline Dumbbell Curl',
      aliases: ['incline curl', 'incline db curl', 'incline dumbbell curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps'],
      secondaryMuscles: [],
      equipment: ['dumbbells', 'incline bench'],
      requiresOneRM: true,
    },
    {
      id: 'cable-curl',
      name: 'Cable Curl',
      aliases: ['cable curl', 'cable bicep curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps'],
      secondaryMuscles: [],
      equipment: ['cable machine'],
      requiresOneRM: true,
    },
    {
      id: 'concentration-curl',
      name: 'Concentration Curl',
      aliases: ['concentration curl', 'seated concentration curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['biceps'],
      secondaryMuscles: [],
      equipment: ['dumbbell'],
      requiresOneRM: true,
    },

    // TRICEP EXERCISES
    {
      id: 'tricep-pushdown',
      name: 'Tricep Pushdown',
      aliases: ['tricep pushdown', 'cable pushdown', 'v-bar pushdown', 'v bar pushdown', 'straight bar pushdown'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['triceps'],
      secondaryMuscles: [],
      equipment: ['cable machine'],
      requiresOneRM: true,
      variations: ['Rope', 'Straight Bar', 'V-Bar', 'Single Arm'],
    },
    {
      id: 'tricep-pulldown',
      name: 'Tricep Pulldown',
      aliases: ['tricep pulldown', 'straight bar tricep pulldown', 'cable tricep pulldown'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['triceps'],
      secondaryMuscles: [],
      equipment: ['cable machine'],
      requiresOneRM: true,
      description: 'Straight bar attachment, overhand grip. Emphasizes lateral head.',
    },
    // 'rope-tricep-pushdown' deleted 2026-07-16 — "Rope Tricep Pushdown"
    // aliases to free-db "Triceps Pushdown - Rope Attachment".
    {
      id: 'overhead-tricep-extension',
      name: 'Overhead Tricep Extension',
      aliases: ['overhead extension', 'tricep overhead extension', 'french press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['triceps'],
      secondaryMuscles: [],
      equipment: ['dumbbell'],
      requiresOneRM: true,
      variations: ['Dumbbell', 'Cable', 'Seated', 'Standing'],
    },
    {
      id: 'skull-crusher',
      name: 'Skull Crusher',
      aliases: ['skull crusher', 'lying tricep extension', 'skullcrusher'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['triceps'],
      secondaryMuscles: [],
      equipment: ['ez bar', 'bench'],
      requiresOneRM: true,
    },
    {
      id: 'dips-tricep',
      name: 'Dips (Tricep Focus)',
      aliases: ['tricep dips', 'bench dips', 'dips'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['triceps'],
      secondaryMuscles: ['chest', 'shoulders'],
      equipment: ['dip bars'],
      requiresOneRM: false,
    },
    {
      id: 'push-ups',
      name: 'Push-Ups',
      aliases: ['push-ups', 'pushups', 'push up', 'pushup'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest', 'triceps'],
      secondaryMuscles: ['shoulders', 'core'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'pull-ups',
      name: 'Pull-Ups',
      aliases: ['pull-ups', 'pullups', 'pull up', 'pullup'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['forearms', 'core'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      force: 'pull',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Hang from a bar with overhand grip, pull your body up until chin clears the bar. The foundational vertical pulling movement.',
      instructions: [
        'Grip a pull-up bar with palms facing away (overhand/pronated grip), hands slightly wider than shoulder-width.',
        'Hang with arms fully extended. Engage your shoulder blades by pulling them down and back (active hang).',
        'Pull yourself up by driving your elbows down toward your hips. Keep your core tight and avoid swinging.',
        'Pull until your chin clears the bar. Squeeze your back muscles at the top.',
        'Lower yourself with control to a full dead hang. Do not drop or use momentum.',
      ],
    },
    {
      id: 'scapular-pull-ups',
      name: 'Scapular Pull-Ups',
      aliases: ['scap pull-ups', 'scapular pull-ups', 'scap pulls', 'active hang'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back', 'shoulders'],
      secondaryMuscles: [],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      description: 'Partial ROM pull-up focusing on scapular retraction/depression — prehab and activation drill',
    },
    {
      id: 'weighted-pull-up',
      name: 'Weighted Pull-Up',
      aliases: ['weighted pull-up', 'weighted pullup', 'weighted pull up', 'weighted pullups'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['forearms', 'core'],
      equipment: ['pull-up bar', 'weight belt'],
      requiresOneRM: true,
    },
    {
      id: 'assisted-pull-up',
      name: 'Assisted Pull-Up',
      // Weight field stores the assistance amount (machine counterbalance in lbs).
      // Higher weight = more help = easier.
      aliases: ['assisted pull-up', 'assisted pullup', 'assisted pull up', 'gravitron pull-up', 'gravitron pullup', 'machine pull-up', 'machine assisted pull-up', 'banded pull-up'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['forearms', 'core'],
      equipment: ['gravitron', 'assisted pull-up machine', 'resistance band'],
      requiresOneRM: false,
      description: 'Pull-up performed with machine or band assistance. Log the assistance amount as the weight (e.g. 50 lbs of counterbalance).',
    },
    {
      id: 'chin-ups',
      name: 'Chin-Ups',
      aliases: ['chin-ups', 'chinups', 'chin up', 'chinup', 'underhand pull-up'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['forearms'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      description: 'Underhand grip pull-up variation',
    },
    {
      id: 'assisted-chin-up',
      name: 'Assisted Chin-Up',
      aliases: ['assisted chin-up', 'assisted chinup', 'assisted chin up', 'gravitron chin-up', 'gravitron chinup', 'machine chin-up', 'banded chin-up'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['forearms'],
      equipment: ['gravitron', 'assisted pull-up machine', 'resistance band'],
      requiresOneRM: false,
      description: 'Chin-up performed with machine or band assistance. Log the assistance amount as the weight.',
    },
    {
      id: 'diamond-push-ups',
      name: 'Diamond Push-Ups',
      aliases: ['diamond push-ups', 'diamond pushups', 'close grip push-ups', 'tricep push-ups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['triceps', 'chest'],
      secondaryMuscles: ['shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Close-grip push-up for triceps',
    },
    {
      id: 'wide-push-ups',
      name: 'Wide Push-Ups',
      aliases: ['wide push-ups', 'wide grip push-ups', 'wide pushups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['shoulders', 'triceps'],
      equipment: [],
      requiresOneRM: false,
      description: 'Wide grip push-up for chest emphasis',
    },
    // 'decline-push-ups' deleted 2026-07-16 — "Decline Push-Ups" aliases to
    // free-db "Decline Push-Up" (the merge was already similarity-dropping
    // this entry, so it contributed nothing at runtime).
    {
      id: 'handstand-push-ups',
      name: 'Handstand Push-Ups',
      aliases: ['handstand push-ups', 'hspu', 'handstand pushups', 'wall hspu'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps', 'core'],
      equipment: ['wall'],
      requiresOneRM: false,
      description: 'Advanced inverted shoulder press',
    },
    {
      id: 'archer-push-ups',
      name: 'Archer Push-Ups',
      aliases: ['archer push-ups', 'archer pushups', 'typewriter push-ups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['shoulders', 'triceps'],
      equipment: [],
      requiresOneRM: false,
      description: 'Unilateral push-up progression',
    },
    {
      id: 'pike-push-ups',
      name: 'Pike Push-Ups',
      aliases: ['pike push-ups', 'pike pushups', 'pike press'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'pseudo-planche-push-ups',
      name: 'Pseudo Planche Push-Ups',
      aliases: ['pseudo planche push-ups', 'planche push-ups', 'pseudo planche'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest', 'shoulders'],
      secondaryMuscles: ['triceps', 'core'],
      equipment: [],
      requiresOneRM: false,
    },
    // 'dips' deleted 2026-07-16 — "Dips" aliases to free-db "Dips - Chest
    // Version"; its bodyweight-reps tracking moved to
    // TRACKING_TYPE_AUDIT_OVERRIDES['dips_-_chest_version'].
    {
      id: 'muscle-ups',
      name: 'Muscle-Ups',
      aliases: ['muscle-ups', 'muscle up', 'muscleup', 'bar muscle-up'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back', 'chest', 'triceps'],
      secondaryMuscles: ['shoulders', 'core'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      description: 'Advanced pull-up to dip transition',
    },
    {
      id: 'australian-pull-ups',
      name: 'Australian Pull-Ups',
      aliases: ['australian pull-ups', 'inverted rows', 'body rows', 'horizontal pull-ups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps'],
      equipment: ['bar'],
      requiresOneRM: false,
      description: 'Horizontal rowing movement',
    },
    {
      id: 'pistol-squats',
      name: 'Pistol Squats',
      aliases: ['pistol squats', 'pistol squat', 'single leg squat', 'one leg squat'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Single-leg squat variation',
    },
    {
      id: 'nordic-curls',
      name: 'Nordic Curls',
      aliases: ['nordic curls', 'nordic hamstring curl', 'natural leg curl'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['hamstrings'],
      secondaryMuscles: ['glutes'],
      equipment: [],
      requiresOneRM: false,
      description: 'Eccentric hamstring strengthening',
    },
    {
      id: 'ring-dips',
      name: 'Ring Dips',
      aliases: ['ring dips', 'gymnastic dips', 'rings dips'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest', 'triceps'],
      secondaryMuscles: ['shoulders', 'core'],
      equipment: ['rings'],
      requiresOneRM: false,
      description: 'Unstable dip variation',
    },
    {
      id: 'ring-rows',
      name: 'Ring Rows',
      aliases: ['ring rows', 'gymnastic rows', 'trx rows'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps', 'core'],
      equipment: ['rings'],
      requiresOneRM: false,
      description: 'Suspended horizontal row',
    },
    {
      id: 'l-sit',
      name: 'L-Sit',
      aliases: ['l-sit', 'l sit hold', 'l-sit hold'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['core', 'abs'],
      secondaryMuscles: ['quads', 'shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Isometric core hold with legs extended',
    },
    {
      id: 'v-ups',
      name: 'V-Ups',
      aliases: ['v-ups', 'v ups', 'pike-ups', 'jackknife'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic core exercise',
    },

    // LEG ACCESSORIES
    {
      id: 'leg-curl',
      name: 'Leg Curl',
      aliases: ['leg curl', 'hamstring curl', 'lying leg curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['hamstrings'],
      secondaryMuscles: [],
      equipment: ['leg curl machine'],
      requiresOneRM: false,
    },
    // 'leg-extension' deleted 2026-07-16 — "Leg Extension" aliases to free-db
    // "Leg Extensions".
    // Walking Lunge (duplicate) → mapped to "Bodyweight Walking Lunge" in free-exercise-db
    // 'calf-raise' deleted 2026-07-16 — "Calf Raise" aliases to free-db
    // "Standing Calf Raises".
    {
      id: 'hip-thrust',
      name: 'Hip Thrust',
      aliases: ['hip thrust', 'barbell hip thrust', 'glute bridge'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['hamstrings'],
      equipment: ['barbell', 'bench'],
      requiresOneRM: false,
    },
    {
      id: 'hip-abduction',
      name: 'Hip Abduction',
      aliases: ['hip abduction', 'abductor machine', 'seated abduction', 'cable hip abduction'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['glutes'],
      secondaryMuscles: [],
      equipment: ['machine'],
      requiresOneRM: false,
    },
    {
      id: 'hip-adduction',
      name: 'Hip Adduction',
      aliases: ['hip adduction', 'adductor machine', 'seated adduction'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['glutes'],
      secondaryMuscles: [],
      equipment: ['machine'],
      requiresOneRM: false,
    },

    // MISCELLANEOUS STRENGTH
    {
      id: 'farmers-carry',
      name: "Farmer's Carry",
      aliases: ['farmers carry', 'farmer carry', 'farmers walk'],
      category: 'strength',
      trackingType: 'carry',
      primaryMuscles: ['forearms', 'core'],
      secondaryMuscles: ['shoulders', 'back'],
      equipment: ['dumbbells'],
      requiresOneRM: false,
      description: 'Track weight carried and distance (meters or feet)',
    },
    {
      id: 'barbell-shrug',
      name: 'Barbell Shrug',
      aliases: ['barbell shrug', 'bb shrug', 'trap shrug barbell'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['forearms'],
      equipment: ['barbell'],
      requiresOneRM: false,
      description: 'Barbell allows heavier loads. Front or behind variations.',
    },
    {
      id: 'dumbbell-shrug',
      name: 'Dumbbell Shrug',
      aliases: ['dumbbell shrug', 'db shrug', 'trap shrug dumbbell'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['forearms'],
      equipment: ['dumbbell'],
      requiresOneRM: false,
      description: 'Dumbbells allow greater range of motion and neutral grip.',
    },
    // 'shrug' deleted 2026-07-16 — "Shrug" aliases to free-db "Barbell Shrug".
    // Override freeExerciseDB - Inverted rows are bodyweight exercises, not weighted
    {
      id: 'inverted-row',
      name: 'Inverted Row',
      aliases: ['inverted row', 'body row', 'horizontal pull-up'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps'],
      equipment: ['barbell', 'smith-machine'],
      requiresOneRM: false,
      description: 'Bodyweight row from underneath a bar',
    },
    {
      id: 'inverted-row-straps',
      name: 'Inverted Row with Straps',
      aliases: ['inverted row with straps', 'trx row', 'ring row', 'suspension row'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps'],
      equipment: ['suspension-trainer'],
      requiresOneRM: false,
      description: 'Bodyweight row using suspension straps',
    },
    {
      id: 'suspended-row',
      name: 'Suspended Row',
      aliases: ['suspended row', 'suspension trainer row'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps'],
      equipment: ['suspension-trainer'],
      requiresOneRM: false,
      description: 'Bodyweight row using suspension system',
    },

    // CHASSIS CUT ADDITIONS
    {
      id: 'leg-press',
      name: 'Leg Press',
      aliases: ['leg press', 'seated leg press', 'machine leg press', 'narrow stance leg press', 'high foot leg press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'calves'],
      equipment: ['leg press machine'],
      requiresOneRM: false,
    },
    {
      id: 'seated-calf-raise',
      name: 'Seated Calf Raise',
      aliases: ['seated calf raise', 'seated calf', 'soleus raise machine'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: ['seated calf machine'],
      requiresOneRM: false,
      description: 'Targets soleus with bent-knee position',
    },
    {
      id: 'seated-ham-curl',
      name: 'Seated Ham Curl',
      aliases: ['seated ham curl', 'seated hamstring curl', 'seated leg curl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['hamstrings'],
      secondaryMuscles: [],
      equipment: ['seated ham curl machine'],
      requiresOneRM: false,
    },
    // 'machine-chest-press' deleted 2026-07-16 — "Machine Chest Press" aliases
    // to free-db "Leverage Chest Press" (per the exercise-DB image audit).
    {
      id: 'cable-lateral-raise',
      name: 'Cable Lateral Raise',
      aliases: ['cable lateral raise', 'cable side raise', 'cable lat raise', 'single arm cable lateral raise'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: [],
      equipment: ['cable machine'],
      requiresOneRM: false,
    },
    {
      id: 'reverse-pec-deck',
      name: 'Reverse Pec Deck',
      aliases: ['reverse pec deck', 'reverse fly machine', 'rear delt machine', 'rear delt fly', 'reverse machine fly'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders', 'back'],
      secondaryMuscles: [],
      equipment: ['pec deck machine'],
      requiresOneRM: false,
      description: 'Rear delt isolation on pec deck machine',
    },
    {
      id: 'landmine-press',
      name: 'Landmine Press',
      aliases: ['landmine press', 'landmine shoulder press', 'single arm landmine press', 'angled barbell press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'], // audit 2026-07-08: chest → secondary
      secondaryMuscles: ['chest', 'triceps', 'core'],
      equipment: ['barbell', 'landmine'],
      requiresOneRM: false,
    },
    {
      id: 'one-arm-cable-row',
      name: '1-Arm Cable Row',
      aliases: ['1 arm cable row', 'one arm cable row', 'single arm cable row', 'single arm row cable'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps', 'core'],
      equipment: ['cable machine'],
      requiresOneRM: false,
    },
    {
      id: 'straight-arm-pulldown',
      name: 'Straight-Arm Pulldown',
      aliases: ['straight arm pulldown', 'straight arm pushdown', 'stiff arm pulldown', 'cable straight arm pulldown'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['core'],
      equipment: ['cable machine'],
      requiresOneRM: false,
      description: 'Lat isolation with arms straight throughout',
    },
    {
      id: 'weighted-chin-up',
      name: 'Weighted Chin-Up',
      aliases: ['weighted chin up', 'weighted chin-up', 'weighted chinup', 'chin up weighted'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['core', 'forearms'],
      equipment: ['pull-up bar', 'weight belt'],
      requiresOneRM: false,
    },
    {
      id: 'tibialis-raise',
      name: 'Tibialis Raise',
      aliases: ['tibialis raise', 'tib raise', 'tibialis anterior raise', 'toe raise', 'tib bar raise'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Lower leg prehab targeting the tibialis anterior',
    },
    {
      id: 'cable-crunch',
      name: 'Cable Crunch',
      aliases: ['cable crunch', 'kneeling cable crunch', 'rope crunch', 'cable ab crunch'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['abs'],
      secondaryMuscles: ['core'],
      equipment: ['cable machine'],
      requiresOneRM: false,
      // Bilateral — both sides crunch together. Explicit so the per-side
      // heuristic (which trips on free-DB variation text) can't flag it as
      // unilateral. Reported 2026-07-02: "Cable crunch is not per side."
      unilateral: false,
    },
    {
      id: 'box-squat',
      name: 'Box Squat',
      aliases: ['box squat', 'box squats', 'seated box squat'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: ['barbell', 'box'],
      requiresOneRM: false,
    },
  ],

  // ============================================================================
  // POWER - Olympic lifts, plyometrics, explosive movements
  // ============================================================================
  power: [
    {
      id: 'power-clean',
      name: 'Power Clean',
      aliases: ['power clean', 'clean', 'hang power clean'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['quads', 'back', 'shoulders'],
      equipment: ['barbell'],
      requiresOneRM: true,
    },
    {
      id: 'power-snatch',
      name: 'Power Snatch',
      aliases: ['power snatch', 'snatch', 'hang power snatch'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['shoulders', 'back', 'quads'],
      equipment: ['barbell'],
      requiresOneRM: false,
    },
    {
      id: 'clean-and-jerk',
      name: 'Clean and Jerk',
      aliases: ['clean and jerk', 'c&j'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['shoulders', 'quads'],
      equipment: ['barbell'],
      requiresOneRM: false,
    },
    // 'box-jump' deleted 2026-07-16 — "Box Jump" was already aliased to
    // free-db "Front Box Jump"; its bodyweight-reps tracking moved to
    // TRACKING_TYPE_AUDIT_OVERRIDES['front_box_jump'].
    {
      id: 'kettlebell-swing',
      name: 'Kettlebell Swing',
      aliases: ['kb swing', 'kettlebell swing', 'swing', 'kb swings'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['glutes', 'hamstrings'],
      secondaryMuscles: ['core', 'shoulders'],
      equipment: ['kettlebell'],
      requiresOneRM: false,
      variations: ['Russian', 'American', 'Single Arm'],
    },
    {
      id: 'kettlebell-goblet-squat',
      name: 'Kettlebell Goblet Squat',
      aliases: ['kb goblet squat', 'kettlebell squat'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['core'],
      equipment: ['kettlebell'],
      requiresOneRM: false,
    },
    {
      id: 'kettlebell-snatch',
      name: 'Kettlebell Snatch',
      aliases: ['kb snatch'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders', 'glutes', 'hamstrings'],
      secondaryMuscles: ['core', 'back'],
      equipment: ['kettlebell'],
      requiresOneRM: false,
    },
    {
      id: 'kettlebell-strict-press',
      name: 'Kettlebell Strict Press',
      aliases: ['kb strict press', 'kb press', 'kettlebell press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['triceps', 'core'],
      equipment: ['kettlebell'],
      requiresOneRM: false,
      unilateral: true, // audit 2026-07-08: pressed one arm at a time
    },
    {
      id: 'burpee',
      name: 'Burpee',
      aliases: ['burpee', 'burpees'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['cardio-specific'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'rope-climb',
      name: 'Rope Climb',
      aliases: ['rope climb', 'rope climbs'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['forearms', 'shoulders', 'core'],
      equipment: ['other'],
      requiresOneRM: false,
      force: 'pull',
      mechanic: 'compound',
      level: 'intermediate',
      description: 'Climb a vertical rope using upper body pulling strength and leg wraps for grip. Track number of climbs or time per climb.',
    },
    {
      id: 'med-ball-scoop-toss',
      name: 'Med Ball Scoop Toss',
      aliases: ['med ball scoop toss', 'medicine ball scoop toss', 'med ball toss'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['glutes', 'core'],
      secondaryMuscles: ['hamstrings', 'shoulders'],
      equipment: ['medicine ball'],
      requiresOneRM: false,
      description: 'Explosive hip-driven underhand toss for horizontal power',
    },
    {
      id: 'med-ball-slam',
      name: 'Med Ball Slam',
      aliases: ['med ball slam', 'medicine ball slam', 'ball slams', 'overhead slam'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['core', 'shoulders'],
      secondaryMuscles: ['back', 'triceps'],
      equipment: ['medicine ball'],
      requiresOneRM: false,
      description: 'Overhead slam to ground for vertical power and core engagement',
    },
  ],

  // ============================================================================
  // TACTICAL - Agility, drills, combatives, tactical movements
  // ============================================================================
  tactical: [
    // Movement + Positions Flow → Too vague, removed (use specific drills like Bear Crawl, 5-10-5 Drill, etc.)
    {
      id: 'bear-crawl',
      name: 'Bear Crawl',
      aliases: ['bear crawl', 'bearcrawl'],
      category: 'tactical',
      trackingType: 'distance-time',
      primaryMuscles: ['core', 'shoulders'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'partner-drag',
      name: 'Partner Drag',
      aliases: ['partner drag', 'drag', 'body drag'],
      category: 'tactical',
      trackingType: 'distance-time', // audit 2026-07-08: dragged over a distance for time
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['back', 'glutes'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'sprint-carry-circuit',
      name: 'Sprint Carry Circuit',
      aliases: ['sprint carry circuit', 'sprint carry', 'carry sprint'],
      category: 'tactical',
      trackingType: 'distance-time',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['cardio-specific'],
      equipment: ['dumbbells', 'kettlebell'],
      requiresOneRM: false,
      description: 'Alternating sprints and loaded carries',
    },
    {
      id: '5-10-5-drill',
      name: '5-10-5 Drill',
      aliases: ['5-10-5', 'pro agility', 'shuttle'],
      category: 'tactical',
      trackingType: 'time',
      primaryMuscles: ['cardio-specific', 'quads'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'tactical-agility',
      name: 'Tactical Agility',
      aliases: ['tactical agility', 'agility drill', 'speed shuffles'],
      category: 'tactical',
      trackingType: 'time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads'],
      equipment: ['plate carrier'],
      requiresOneRM: false,
    },
    {
      id: 'combat-circuit',
      name: '5-Station Combat Circuit',
      aliases: ['combat circuit', '5 station circuit', 'sealgrinz'],
      category: 'tactical',
      trackingType: 'circuit',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['cardio-specific'],
      equipment: ['various'],
      requiresOneRM: false,
    },
    {
      id: 'broad-jumps',
      name: 'Broad Jumps',
      aliases: ['broad jumps', 'broad jump', 'standing long jump', 'horizontal jump'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['calves', 'hamstrings'],
      equipment: [],
      requiresOneRM: false,
      description: 'Horizontal explosive power',
    },
    {
      id: 'sled-push',
      name: 'Sled Push',
      aliases: ['sled push', 'prowler push', 'sled work'],
      category: 'carry', // was 'tactical' — trackingType=carry, so the category should match
      trackingType: 'carry',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['calves', 'core'],
      equipment: ['sled'],
      requiresOneRM: false,
      description: 'Heavy sled pushing',
    },
    {
      id: 'sled-pull',
      name: 'Sled Pull',
      aliases: ['sled pull', 'prowler pull', 'sled drag'],
      category: 'carry', // was 'tactical' — trackingType=carry, so the category should match
      trackingType: 'carry',
      primaryMuscles: ['hamstrings'], // audit 2026-07-08: backward drag on harness → hamstring-dominant
      secondaryMuscles: ['glutes', 'back', 'core'],
      equipment: ['sled'],
      requiresOneRM: false,
      description: 'Backward sled drag on a harness (hamstring-dominant)',
    },
    // Tire Flips → mapped to "Tire Flip" in free-exercise-db (has images)
    // Battle Ropes → mapped to "Battling Ropes" in free-exercise-db (has images)
    {
      id: 'wall-balls',
      name: 'Wall Balls',
      aliases: ['wall balls', 'wall ball shots', 'med ball throws'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'shoulders'],
      secondaryMuscles: ['core'],
      equipment: ['medicine ball', 'wall'],
      requiresOneRM: false,
      description: 'Squat to overhead throw',
    },
    // Medicine Ball Slams → mapped to "Overhead Slam" in free-exercise-db (has images)
    {
      id: 'sledgehammer-swings',
      name: 'Sledgehammer Swings',
      aliases: ['sledgehammer swings', 'sledge swings', 'tire hits'],
      category: 'tactical',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['core', 'shoulders'],
      secondaryMuscles: ['back'],
      equipment: ['sledgehammer', 'tire'],
      requiresOneRM: false,
      description: 'Rotational power and conditioning',
    },
    {
      id: 'burpee-box-jumps',
      name: 'Burpee Box Jumps',
      aliases: ['burpee box jumps', 'burpee box jump', 'box jump burpee'],
      category: 'tactical',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['cardio-specific'],
      equipment: ['box'],
      requiresOneRM: false,
      description: 'Burpee combined with box jump',
    },
    {
      id: 'shuttle-runs',
      name: 'Shuttle Runs',
      aliases: ['shuttle runs', 'shuttle run', 'suicide runs', 'beep test'],
      category: 'tactical',
      trackingType: 'time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
      description: 'Multi-directional sprint drill',
    },
    {
      id: 'sandbag-cleans',
      name: 'Sandbag Cleans',
      aliases: ['sandbag clean', 'sandbag cleans', 'bag cleans'],
      category: 'power',
      trackingType: 'weight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: [],
      equipment: ['sandbag'],
      requiresOneRM: false,
      description: 'Explosive sandbag lifting',
    },
    {
      id: 'sandbag-overhead-press',
      name: 'Sandbag Overhead Press',
      aliases: ['sandbag press', 'sandbag ohp', 'bag press'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['core', 'triceps'],
      equipment: ['sandbag'],
      requiresOneRM: false,
      description: 'Unstable overhead pressing',
    },
  ],

  // ============================================================================
  // CARDIO - Running, rowing, biking, swimming (continuous)
  // ============================================================================
  cardio: [
    {
      id: 'easy-cardio',
      name: 'Easy Cardio',
      aliases: ['easy cardio', 'light cardio', 'warmup cardio'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: [],
      equipment: ['bike', 'rower', 'treadmill'],
      requiresOneRM: false,
      description: 'Easy pace cardio for warm-up or active recovery - bike, row, or jog',
    },
    {
      id: 'run',
      name: 'Run',
      aliases: ['run', 'running', 'jog', 'jogging', '2 mile run', 'interval run', 'run or ruck'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'calves'],
      equipment: [],
      requiresOneRM: false,
      variations: ['Easy', 'Tempo', 'Interval', 'Long Slow Distance', '2 Mile', 'Interval'],
    },
    {
      id: 'sprint',
      name: 'Sprint',
      aliases: ['sprint', 'sprints', 'dash'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific', 'quads'],
      secondaryMuscles: ['glutes', 'calves'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'track-intervals',
      name: 'Track Intervals',
      aliases: ['track intervals', 'interval sprints', 'sprint intervals'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
    },
    // Rowing → mapped to "Rowing, Stationary" in free-exercise-db (has images)
    {
      id: 'row-bike-sprint',
      name: 'Row/Bike Sprint',
      aliases: ['row/bike sprint', 'row bike sprint', 'bike sprint', 'row sprint'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'back'],
      equipment: ['rowing machine', 'assault bike'],
      requiresOneRM: false,
      description: 'High-intensity intervals on rower or bike',
    },
    {
      id: 'assault-bike',
      name: 'Assault Bike',
      aliases: ['assault bike', 'air bike', 'echo bike', 'bike'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads'],
      equipment: ['assault bike'],
      requiresOneRM: false,
    },
    {
      id: 'ski-erg',
      name: 'Ski Erg',
      aliases: ['ski erg', 'skierg', 'ski'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific', 'shoulders'],
      secondaryMuscles: ['core', 'back'],
      equipment: ['ski erg'],
      requiresOneRM: false,
    },
    {
      id: 'treadmill-incline-walk',
      name: 'Treadmill Incline Walk',
      aliases: ['treadmill incline walk', 'incline walk', 'treadmill incline', 'incline treadmill'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific', 'glutes', 'hamstrings'],
      secondaryMuscles: ['quads', 'calves'],
      equipment: ['treadmill'],
      requiresOneRM: false,
      description: 'Walking on an inclined treadmill (0-15% grade). Higher inclines significantly increase difficulty and caloric expenditure.',
      tags: ['incline', 'treadmill', 'walking', 'low-impact'],
    },
    {
      id: 'jump-rope',
      name: 'Jump Rope',
      aliases: ['jump rope', 'skipping', 'double unders'],
      category: 'cardio',
      trackingType: 'time',
      primaryMuscles: ['cardio-specific', 'calves'],
      secondaryMuscles: [],
      equipment: ['jump rope'],
      requiresOneRM: false,
    },
    
    // HIIT Intervals - Per-interval distance tracking
    {
      id: 'hiit-30-60',
      name: '30/60s Sprint',
      aliases: ['30:60s hiit intervals', '30:60 hiit', '30/60 hiit', 'hiit 30:60', '30/60s sprint', '30-60 sprint', '30/60 intervals'],
      category: 'cardio',
      trackingType: 'interval',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
      description: '30 seconds ALL OUT sprint, 60 seconds walk/rest. Treadmill or outside.',
    },
    {
      id: 'hiit-60-120',
      name: '60/120s Sprint',
      aliases: ['60:120s hiit intervals', '60:120 hiit', '60/120 hiit', 'hiit 60:120', '60/120s sprint', '60-120 sprint', '60/120 intervals'],
      category: 'cardio',
      trackingType: 'interval',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
      description: '60 seconds ALL OUT sprint, 120 seconds walk/rest. Treadmill or outside.',
    },
    {
      id: 'tempo-run',
      name: 'Tempo Run',
      aliases: ['tempo run', 'threshold run', 'tempo'],
      category: 'cardio',
      trackingType: 'circuit',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'glutes'],
      equipment: [],
      requiresOneRM: false,
      description: 'Sustained moderate-hard pace (75-80% effort). Track checkpoints for pacing.',
    },
    {
      id: 'walk',
      name: 'Walk',
      aliases: ['walk', 'walking', 'easy walk', 'brisk walk'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'calves'],
      equipment: [],
      requiresOneRM: false,
      description: 'Low-intensity walking for active recovery',
    },
    {
      id: 'trail-walk',
      name: 'Trail Walk',
      aliases: ['trail walk', 'hiking', 'nature walk'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'glutes', 'calves'],
      equipment: [],
      requiresOneRM: false,
      description: 'Walking on trails or uneven terrain',
    },
    {
      id: 'easy-run',
      name: 'Easy Run',
      aliases: ['easy run', 'conversational pace', 'aerobic run'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'calves'],
      equipment: [],
      requiresOneRM: false,
      description: 'Conversational pace, aerobic zone',
    },
    {
      id: 'tempo-run',
      name: 'Tempo Run',
      aliases: ['tempo run', 'threshold run', 'steady state'],
      category: 'cardio',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['quads', 'calves'],
      equipment: [],
      requiresOneRM: false,
      description: 'Moderately hard pace, lactate threshold',
    },
    // Fartlek → removed (audit 2026-07-08: unused, redundant with interval run)
    // Long Run → redundant with "Run" exercise (use "Run" with longer distance)
  ],

  // ============================================================================
  // RUCK - Loaded marches only (carries moved to their own category below)
  // ============================================================================
  ruck: [
    {
      id: 'ruck',
      name: 'Ruck',
      aliases: ['ruck', 'rucking', 'ruck march', 'weighted walk'],
      category: 'ruck',
      trackingType: 'ruck',
      primaryMuscles: ['cardio-specific', 'back'],
      secondaryMuscles: ['quads', 'glutes'],
      equipment: ['rucksack'],
      requiresOneRM: false,
    },
    {
      id: 'ruck-intervals',
      name: 'Ruck Intervals',
      aliases: ['ruck intervals', 'interval ruck', 'ruck interval training'],
      category: 'ruck',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific', 'back'],
      secondaryMuscles: ['quads', 'glutes'],
      equipment: ['rucksack'],
      requiresOneRM: false,
      description: 'Alternating pace ruck intervals',
    },
  ],

  // ============================================================================
  // CARRY - Loaded carries (farmer/yoke/suitcase/overhead/zercher/sled walks)
  // Tracking type 'carry' uses CarryInputRow (load + distance + sets + RPE).
  // Distinct from 'ruck' which is for sustained loaded marches with rucksack.
  // ============================================================================
  carry: [
    {
      id: 'sandbag-carry',
      name: 'Sandbag Carry',
      aliases: ['sandbag carry', 'sandbag walk'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['core', 'shoulders'],
      equipment: ['sandbag'],
      requiresOneRM: false,
    },
    // Override freeExerciseDB — these should be 'carry' tracking + 'carry' category
    {
      id: 'farmers-walk',
      name: "Farmer's Walk",
      aliases: ['farmer walk', 'farmers walk', 'farmer carry', 'farmers carry'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['forearms', 'back'],
      secondaryMuscles: ['core', 'quads', 'glutes'],
      equipment: ['dumbbells', 'kettlebells', 'trap bar'],
      requiresOneRM: false,
      description: 'Walk while holding heavy weights at your sides',
    },
    {
      id: 'yoke-walk',
      name: 'Yoke Walk',
      aliases: ['yoke walk', 'yoke carry'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['full-body', 'back'],
      secondaryMuscles: ['core', 'quads', 'glutes'],
      equipment: ['yoke'],
      requiresOneRM: false,
      description: 'Walk with a yoke across the shoulders',
    },
    {
      id: 'suitcase-carry',
      name: 'Suitcase Carry',
      aliases: ['suitcase carry', 'suitcase walk', 'one arm carry', 'unilateral carry', 'single-arm carry'],
      category: 'carry',
      trackingType: 'carry',
      unilateral: true, // explicit — one-side-loaded carry, anti-lateral flexion
      primaryMuscles: ['core', 'obliques'],
      secondaryMuscles: ['back', 'forearms', 'shoulders'],
      equipment: ['dumbbell', 'kettlebell'],
      requiresOneRM: false,
      description: 'Single-arm carry for anti-lateral flexion. Track weight per hand and distance per side.',
    },
    {
      id: 'overhead-carry',
      name: 'Overhead Carry',
      aliases: ['overhead carry', 'overhead walk', 'waiter carry'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['shoulders', 'core'],
      secondaryMuscles: ['back', 'triceps'],
      equipment: ['dumbbell', 'kettlebell', 'barbell'],
      requiresOneRM: false,
      description: 'Walk with weight locked out overhead',
    },
    {
      id: 'zercher-carry',
      name: 'Zercher Carry',
      aliases: ['zercher carry', 'zercher walk'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['core', 'biceps'],
      secondaryMuscles: ['back', 'quads'],
      equipment: ['barbell', 'sandbag'],
      requiresOneRM: false,
      description: 'Walk with weight held in the crooks of the elbows',
    },
    {
      id: 'rickshaw-carry',
      name: 'Rickshaw Carry',
      aliases: ['rickshaw carry', 'rickshaw walk', 'frame carry'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['forearms', 'back'],
      secondaryMuscles: ['core', 'quads', 'glutes'],
      equipment: ['rickshaw', 'frame'],
      requiresOneRM: false,
      description: 'Walk while holding a rickshaw/frame at your sides',
    },
    // Sled exercises — moved from 'tactical' to 'carry' since they share
    // the carry tracking shape (load + distance + sets).
    {
      id: 'sled-drag',
      name: 'Sled Drag - Harness',
      aliases: ['sled drag', 'sled drag harness', 'harness drag'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'calves', 'core'],
      equipment: ['sled', 'harness'],
      requiresOneRM: false,
      description: 'Drag a sled using a harness attachment',
    },
    {
      id: 'backward-drag',
      name: 'Backward Drag',
      aliases: ['backward sled drag', 'reverse sled drag', 'backward drag'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['quads'],
      secondaryMuscles: ['glutes', 'calves'],
      equipment: ['sled'],
      requiresOneRM: false,
      description: 'Walk backwards while dragging a sled',
    },
    {
      id: 'bear-crawl-sled-drags',
      name: 'Bear Crawl Sled Drags',
      aliases: ['bear crawl sled drag', 'bear crawl drag'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['core', 'shoulders'],
      secondaryMuscles: ['quads', 'chest'],
      equipment: ['sled'],
      requiresOneRM: false,
      description: 'Bear crawl position while dragging a sled',
    },
    {
      id: 'sled-overhead-backward-walk',
      name: 'Sled Overhead Backward Walk',
      aliases: ['sled overhead walk', 'overhead sled drag'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['shoulders', 'core'],
      secondaryMuscles: ['quads', 'back'],
      equipment: ['sled'],
      requiresOneRM: false,
      description: 'Walk backwards with sled while holding arms overhead',
    },
    {
      id: 'bottoms-up-kb-carry',
      name: 'Bottoms-Up KB Carry',
      aliases: ['bottoms up kb carry', 'bottoms-up kettlebell carry', 'bottoms up carry', 'kb bottoms up walk'],
      category: 'carry',
      trackingType: 'carry',
      primaryMuscles: ['shoulders', 'forearms'],
      secondaryMuscles: ['core'],
      equipment: ['kettlebell'],
      requiresOneRM: false,
      description: 'Carry kettlebell inverted (bottom-up) for grip and shoulder stability',
    },
  ],

  // ============================================================================
  // SWIM - Swimming variations
  // ============================================================================
  swim: [
    // Continuous swimming (distance-time)
    {
      id: 'swim',
      name: 'Swim',
      aliases: ['swim', 'swimming', 'freestyle', 'lap', 'continuous swim', 'straight swim'],
      category: 'swim',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['back', 'shoulders'],
      equipment: ['pool'],
      requiresOneRM: false,
      variations: ['Freestyle', 'Breaststroke', 'Butterfly', 'Backstroke'],
    },
    {
      id: 'hypoxic-swim',
      name: 'Hypoxic Swim',
      aliases: ['hypoxic', 'hypoxic swim', 'breath control'],
      category: 'swim',
      trackingType: 'distance-time',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['back'],
      equipment: ['pool'],
      requiresOneRM: false,
      description: 'Continuous swim with controlled breathing',
    },
    // Pool sets (swim-sets tracking type for per-set data)
    {
      id: 'swim-intervals',
      name: 'Swim Intervals',
      aliases: ['intervals', 'swim set', 'swim sets', 'pool sets', 'interval set'],
      category: 'swim',
      trackingType: 'swim-sets',
      primaryMuscles: ['cardio-specific'],
      secondaryMuscles: ['back', 'shoulders'],
      equipment: ['pool'],
      requiresOneRM: false,
      description: 'Interval training with rest between sets (e.g., 5×100m @ 1:45)',
    },
    // Sprint Sets → redundant with "Swim Intervals" (use "Swim Intervals" with higher intensity)
  ],

  // ============================================================================
  // CIRCUIT - Circuits, AMRAPs, EMOMs
  // ============================================================================
  circuit: [
    {
      id: 'sealgrinz',
      name: 'SealGrinz',
      aliases: ['sealgrinz', 'seal grins', 'seal grinz'],
      category: 'circuit',
      trackingType: 'circuit',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['cardio-specific'],
      equipment: ['various'],
      requiresOneRM: false,
    },
    {
      id: 'operator-test-amrap',
      name: 'Operator Test AMRAP',
      aliases: ['operator amrap', 'test amrap', 'amrap'],
      category: 'circuit',
      trackingType: 'amrap',
      primaryMuscles: ['full-body'],
      secondaryMuscles: ['cardio-specific'],
      equipment: [],
      requiresOneRM: false,
      description: '5 pull-ups, 10 push-ups, 15 air squats, 200m run',
    },
  ],

  // ============================================================================
  // MOBILITY - Stretching, yoga, flow work
  // ============================================================================
  mobility: [
    {
      id: 'dynamic-prep',
      name: 'Dynamic Prep',
      aliases: ['dynamic prep', 'warm up', 'dynamic warmup', 'activation'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['full-body'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
    },
    // Static holds - time tracking
    {
      id: 'dead-hang',
      name: 'Dead Hang',
      aliases: ['dead hang', 'bar hang', 'passive hang', 'hanging'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['back', 'forearms'],
      secondaryMuscles: ['shoulders', 'biceps'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      description: 'Hang from a bar with arms fully extended to decompress spine and improve grip',
    },
    {
      id: 'wall-sit',
      name: 'Wall Sit',
      aliases: ['wall sit', 'wall squat', 'wall squat hold'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['quads'],
      secondaryMuscles: ['glutes', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Isometric hold against a wall with thighs parallel to ground',
    },
    {
      id: 'l-sit-hold',
      name: 'L-Sit Hold',
      aliases: ['l-sit', 'l sit', 'l-sit hold'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['core', 'triceps'],
      secondaryMuscles: ['shoulders', 'quads'],
      equipment: ['parallettes', 'dip bars'],
      requiresOneRM: false,
      description: 'Support body with legs extended parallel to ground',
    },
    {
      id: 'leg-swing',
      name: 'Leg Swing',
      // Prescribed as a warm-up drill by several programs with no DB entry, so
      // findExercise fuzzy-matched it to KETTLEBELL SWING on the word "swing" —
      // putting a loaded ballistic hip hinge into a mobility block. A real
      // entry stops the fuzzy matcher from having to guess.
      aliases: ['leg swing', 'leg swings', 'front to back leg swing', 'lateral leg swing', 'hip swing'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['hamstrings'],
      secondaryMuscles: ['glutes'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic hip mobility drill — controlled swings front-to-back and side-to-side to open the hips before lifting or running',
    },
    {
      id: 'mobility-reset',
      name: 'Mobility Reset',
      aliases: ['mobility reset', 'mobility', 'cooldown', 'stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['full-body'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'foam-rolling',
      name: 'Foam Rolling',
      aliases: ['foam rolling', 'foam roll', 'smr', 'myofascial release'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['full-body'],
      secondaryMuscles: [],
      equipment: ['foam roller'],
      requiresOneRM: false,
    },
    {
      id: 'hip-mobility-flow',
      name: 'Hip Mobility Flow',
      aliases: ['hip mobility flow', 'hip mobility', 'hip flow', 'hip stretches'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes', 'hamstrings'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic hip mobility sequence: 90/90 hip rotations, frog stretch, pigeon pose, leg swings (10 each direction), hip circles, deep squats. Flow through for 5-10 minutes.',
      instructions: [
        'Start with 90/90 position (both knees at 90°), alternate sides for 30s each',
        'Move into frog stretch, rock back and forth for 30s',
        'Transition to pigeon pose on each side, hold 30s',
        'Stand and perform leg swings: forward/back and side-to-side, 10 each leg',
        'Hip circles: 10 in each direction per leg',
        'Finish with deep squat holds, rocking side to side, 1 minute',
        'Repeat sequence 1-2 times for full session',
      ],
    },
    {
      id: 'hip-opener-flow',
      name: 'Hip Opener Flow',
      aliases: ['hip opener flow', 'hip opener', 'hip openers'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['glutes', 'hamstrings'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic hip opening sequence',
    },
    {
      id: 'shoulder-ytw',
      name: 'Shoulder Y-T-W',
      aliases: ['shoulder y-t-w', 'ytw', 'y-t-w', 'shoulder ytw'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders', 'back'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Shoulder prehab and stability exercise',
    },
    {
      id: 'plank-variations',
      name: 'Plank Variations',
      aliases: ['plank variations', 'plank variation', 'various planks'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['core'],
      secondaryMuscles: ['shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Mix of plank positions and holds',
    },
    // STATIC STRETCHES
    {
      id: 'hamstring-stretch',
      name: 'Hamstring Stretch',
      aliases: ['hamstring stretch', 'seated hamstring stretch', 'standing hamstring stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['hamstrings'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Static hamstring flexibility',
    },
    // Quad Stretch → mapped to "Standing Elevated Quad Stretch" in free-exercise-db (has images)
    {
      id: 'hip-flexor-stretch',
      name: 'Hip Flexor Stretch',
      aliases: ['hip flexor stretch', 'couch stretch', 'kneeling hip flexor', 'lunge stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
      description: 'Stretch for hip flexors and psoas',
    },
    {
      id: 'pigeon-stretch',
      name: 'Pigeon Stretch',
      aliases: ['pigeon stretch', 'pigeon pose', 'figure-4 stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['hamstrings'],
      equipment: [],
      requiresOneRM: false,
      description: 'Deep hip and glute stretch',
    },
    // Calf Stretch → mapped to "Seated Calf Stretch" in free-exercise-db (has images)
    {
      id: 'chest-doorway-stretch',
      name: 'Chest Doorway Stretch',
      aliases: ['chest stretch', 'doorway stretch', 'pec stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Open chest and anterior shoulder',
    },
    {
      id: 'shoulder-sleeper-stretch',
      name: 'Shoulder Sleeper Stretch',
      aliases: ['sleeper stretch', 'internal rotation stretch', 'shoulder ir stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Internal shoulder rotation mobility',
    },
    // Cross-Body Shoulder Stretch → mapped to "Shoulder Stretch" in free-exercise-db (has images)
    // Overhead Tricep Stretch → mapped to "Triceps Stretch" in free-exercise-db (has images)
    {
      id: 'lat-stretch',
      name: 'Lat Stretch',
      aliases: ['lat stretch', 'latissimus stretch', 'side lat stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['back'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Latissimus dorsi flexibility',
    },
    {
      id: 'childs-pose',
      name: 'Child\'s Pose',
      aliases: ['childs pose', 'child pose', 'balasana'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['back'],
      secondaryMuscles: ['shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Resting yoga pose for back and shoulders',
    },
    {
      id: 'cat-cow',
      name: 'Cat-Cow',
      aliases: ['cat cow', 'cat-cow stretch', 'spinal flexion extension'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['core'],
      secondaryMuscles: ['back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic spinal mobility',
    },
    {
      id: 'thoracic-rotation',
      name: 'Thoracic Rotation',
      aliases: ['thoracic rotation', 't-spine rotation', 'upper back rotation'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Upper back rotational mobility',
    },
    // World's Greatest Stretch → mapped to "World's Greatest Stretch" in free-exercise-db (has images)
    // 90/90 Hip Stretch → mapped to "90/90 Hamstring" in free-exercise-db (has images)
    {
      id: 'frog-stretch',
      name: 'Frog Stretch',
      aliases: ['frog stretch', 'adductor stretch', 'groin stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Deep hip and adductor stretch',
    },
    {
      id: 'scorpion-stretch',
      name: 'Scorpion Stretch',
      aliases: ['scorpion', 'scorpion stretch', 'lying scorpion'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['glutes'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic hip and spinal rotation',
    },
    {
      id: 'couch-stretch',
      name: 'Couch Stretch',
      aliases: ['couch stretch', 'wall quad stretch', 'rectus femoris stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['quads'],
      secondaryMuscles: ['glutes'],
      equipment: [],
      requiresOneRM: false,
      description: 'Deep hip flexor and quad stretch',
    },
    {
      id: 'wall-pec-stretch',
      name: 'Wall Pec Stretch',
      aliases: ['wall pec stretch', 'corner chest stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Deep chest and anterior shoulder stretch',
    },
    {
      id: 'banded-shoulder-dislocate',
      name: 'Banded Shoulder Dislocate',
      aliases: ['shoulder dislocate', 'banded dislocate', 'pass through'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['chest'],
      equipment: ['band'],
      requiresOneRM: false,
      description: 'Shoulder mobility and flexibility',
    },
    {
      id: 'downward-dog',
      name: 'Downward Dog',
      aliases: ['downward dog', 'down dog', 'adho mukha svanasana'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['hamstrings', 'calves'],
      secondaryMuscles: ['shoulders', 'back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Classic yoga pose for posterior chain',
    },

    // CHASSIS CUT — PREHAB / MOBILITY ADDITIONS
    {
      id: 'ankle-rocks',
      name: 'Ankle Rocks',
      aliases: ['ankle rocks', 'ankle rock', 'ankle mobility rocks'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Ankle dorsiflexion mobilization — rock forward over toes',
    },
    {
      id: 'spanish-squat-iso',
      name: 'Spanish Squat Iso',
      aliases: ['spanish squat', 'spanish squat iso', 'banded spanish squat', 'spanish squat hold'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['quads'],
      secondaryMuscles: ['glutes'],
      equipment: ['band'],
      requiresOneRM: false,
      description: 'Banded isometric squat for knee prehab and quad loading',
    },
    {
      id: 'tke',
      name: 'TKE',
      aliases: ['tke', 'terminal knee extension', 'banded knee extension', 'banded tke'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads'],
      secondaryMuscles: [],
      equipment: ['band'],
      requiresOneRM: false,
      description: 'Terminal knee extension with band — VMO and knee prehab',
    },
    {
      id: 'band-lateral-walk',
      name: 'Band Lateral Walk',
      aliases: ['band lateral walk', 'lateral band walk', 'monster walk', 'banded side walk', 'band walk'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['glutes'],
      secondaryMuscles: [],
      equipment: ['band'],
      requiresOneRM: false,
      description: 'Glute med activation with band around ankles or knees',
    },
    {
      id: 'step-downs',
      name: 'Step-Downs',
      aliases: ['step downs', 'step-downs', 'eccentric step down', 'step down'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads'],
      secondaryMuscles: ['glutes'],
      equipment: ['box'],
      requiresOneRM: false,
      description: 'Slow eccentric step-down for knee prehab and quad control',
    },
    {
      id: 'band-external-rotation',
      name: 'Band External Rotation',
      aliases: ['band er', 'band external rotation', 'banded external rotation', 'shoulder external rotation', 'er band'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: [],
      equipment: ['band'],
      requiresOneRM: false,
      description: 'Rotator cuff prehab — external rotation with band',
    },
    {
      id: 't-spine-extension',
      name: 'T-Spine Extension',
      aliases: ['t spine extension', 't-spine extension', 'thoracic extension', 'foam roller t-spine extension'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: [],
      equipment: ['foam roller'],
      requiresOneRM: false,
      description: 'Thoracic spine extension over foam roller',
    },
    {
      id: 'serratus-wall-slide',
      name: 'Serratus Wall Slide',
      aliases: ['serratus wall slide', 'wall slide', 'serratus slide', 'wall slides'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders'],
      secondaryMuscles: ['back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Scapular upward rotation and serratus anterior activation',
    },
    {
      id: 'hip-hinge-drill',
      name: 'Hip Hinge Drill',
      aliases: ['hip hinge drill', 'hip hinge', 'hinge drill', 'dowel hip hinge'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['hamstrings', 'glutes'],
      secondaryMuscles: ['back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Movement pattern drill for deadlift/RDL hinge',
    },
    {
      id: 'glute-bridge-iso',
      name: 'Glute Bridge Iso',
      aliases: ['glute bridge iso', 'glute bridge hold', 'glute bridge isometric', 'bridge hold'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['hamstrings'],
      equipment: [],
      requiresOneRM: false,
      description: 'Isometric glute bridge hold for activation',
    },
    {
      id: 'soleus-raise',
      name: 'Soleus Raise',
      aliases: ['soleus raise', 'soleus raises', 'bent knee calf raise', 'soleus calf raise'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Bent-knee calf raise targeting the soleus',
    },
    {
      id: 'split-stance-hinge',
      name: 'Split-Stance Hinge',
      aliases: ['split stance hinge', 'split-stance hinge', 'staggered stance rdl', 'b-stance hinge'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['hamstrings', 'glutes'],
      secondaryMuscles: ['back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Unilateral hip hinge prep in staggered stance',
    },
    {
      id: 'worlds-greatest-stretch',
      name: "World's Greatest Stretch",
      aliases: ['worlds greatest stretch', "world's greatest stretch", 'wgs', 'spiderman lunge rotation'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['full-body'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Full-body dynamic stretch — lunge + rotation + hamstring',
    },
    {
      id: 'hip-flexor-opener',
      name: 'Hip Flexor Opener',
      aliases: ['hip flexor opener', 'hip opener', 'standing hip flexor opener'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['quads', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Dynamic hip flexor mobilization — hold 30-45s per side',
    },
    {
      id: 'hip-airplanes',
      name: 'Hip Airplanes',
      aliases: ['hip airplanes', 'hip airplane', 'single leg hip airplane'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Single-leg hip rotation for stability and mobility',
    },
    {
      id: 'open-books',
      name: 'Open Books',
      aliases: ['open books', 'open book stretch', 'side-lying rotation', 'open book rotation'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['chest'],
      equipment: [],
      requiresOneRM: false,
      description: 'Side-lying thoracic rotation for t-spine mobility',
    },
    {
      id: '90-90-breathing',
      name: '90/90 Breathing',
      aliases: ['90 90 breathing', '90/90 breathing', 'diaphragmatic breathing', '90-90 breathing'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['core'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Parasympathetic breathing drill — legs on wall/box at 90 degrees',
    },
    {
      id: 'soleus-stretch',
      name: 'Soleus Stretch',
      aliases: ['soleus stretch', 'bent knee calf stretch', 'soleus calf stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Bent-knee calf stretch targeting the soleus muscle',
    },
    {
      id: 'calf-stretch',
      name: 'Calf Stretch',
      aliases: ['calf stretch', 'gastrocnemius stretch', 'wall calf stretch', 'straight leg calf stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Straight-leg calf stretch for the gastrocnemius',
    },
    {
      id: 'glute-stretch',
      name: 'Glute Stretch',
      aliases: ['glute stretch', 'seated glute stretch', 'figure 4 stretch', 'piriformis stretch'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['glutes'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Glute and piriformis stretch',
    },
  ],

  // ============================================================================
  // CORE - Planks, carries, anti-rotation
  // ============================================================================
  core: [
    {
      id: 'plank',
      name: 'Plank',
      aliases: ['plank', 'front plank', 'forearm plank', 'plank hold'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['core'],
      secondaryMuscles: ['shoulders'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'side-plank',
      name: 'Side Plank',
      aliases: ['side plank', 'lateral plank'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['obliques', 'core'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      unilateral: true, // timed hold, one side at a time
    },
    {
      id: 'copenhagen-plank',
      name: 'Copenhagen Plank',
      aliases: ['copenhagen plank', 'copenhagen hold', 'adductor plank'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['core', 'obliques'],
      secondaryMuscles: ['glutes'],
      equipment: ['bench'],
      requiresOneRM: false,
      description: 'Adductor stability exercise',
    },
    {
      id: 'ab-wheel',
      name: 'Ab Wheel',
      aliases: ['ab wheel', 'ab rollout', 'wheel rollout'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: ['shoulders'],
      equipment: ['ab wheel'],
      requiresOneRM: false,
    },
    {
      id: 'hanging-leg-raise',
      name: 'Hanging Leg Raise',
      aliases: ['hanging leg raise', 'leg raise', 'hlr'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs'],
      secondaryMuscles: ['core'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
    },
    {
      id: 'decline-sit-up',
      name: 'Decline Sit-Up',
      aliases: ['decline sit up', 'decline situp', 'weighted decline sit up', 'decline crunch'],
      category: 'core',
      trackingType: 'weight-reps',
      primaryMuscles: ['abs'],
      secondaryMuscles: ['core'],
      equipment: ['decline bench'],
      requiresOneRM: false,
      description: 'Sit-up performed on decline bench, can be weighted with plate on chest',
    },
    // Suitcase Carry moved to EXERCISE_DATABASE.carry (canonical home).
    // The duplicate that lived here used to win the dedup race and force
    // category='core' on the merged entry.
    {
      id: 'pallof-press',
      name: 'Pallof Press',
      aliases: ['pallof press', 'pallof', 'anti-rotation press'],
      category: 'core',
      trackingType: 'weight-reps',
      primaryMuscles: ['core', 'obliques'],
      secondaryMuscles: ['shoulders', 'triceps'],
      equipment: ['cable machine'],
      requiresOneRM: false,
      unilateral: true, // anti-rotation — pressed out per side
      description: 'Anti-rotation core exercise',
    },
    {
      id: 'box-breathing',
      name: 'Box Breathing',
      aliases: ['box breathing', 'breath work', 'tactical breathing'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['core'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: '4-4-4-4 breathing pattern for recovery and mental clarity',
    },
    {
      id: 'active-recovery',
      name: 'Active Recovery',
      aliases: ['active recovery', 'light movement', 'recovery day'],
      category: 'mobility',
      trackingType: 'time',
      primaryMuscles: ['full-body'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Light movement, stretching, foam rolling, mobility work',
    },
    // Technical Drills → Too vague, removed (use specific swim drills or form work exercises)
    // Movement + Positions + Conditioning → Too vague, removed (use specific tactical drills)
    {
      id: 'hollow-body-hold',
      name: 'Hollow Body Hold',
      aliases: ['hollow body hold', 'hollow hold', 'hollow body', 'hollow rock hold'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: ['quads'],
      equipment: [],
      requiresOneRM: false,
      description: 'Calisthenics core isometric - lower back pressed into floor, legs and shoulders raised',
    },
    {
      id: 'dragon-flag',
      name: 'Dragon Flag',
      aliases: ['dragon flag', 'dragon flags', 'bruce lee flag'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: ['back'],
      equipment: ['bench'],
      requiresOneRM: false,
      description: 'Advanced core exercise - entire body pivots from shoulders',
    },
    {
      id: 'crunches',
      name: 'Crunches',
      aliases: ['crunches', 'crunch', 'abdominal crunch', 'ab crunch'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Basic abdominal exercise',
    },
    // 'back-extension' deleted 2026-07-16 — "Back Extension" was already
    // aliased to free-db "Glute Ham Raise" (exerciseCanonicalMap.json), which
    // made this entry unreachable by name; GHR is weight-reps already.
    {
      id: 'l-sit-hold',
      name: 'L-Sit Hold',
      aliases: ['l-sit hold', 'l sit hold', 'l-sit', 'l sit'],
      category: 'core',
      trackingType: 'time',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: ['quads', 'triceps'],
      equipment: ['parallettes'],
      requiresOneRM: false,
      description: 'Calisthenics hold with legs extended horizontally',
    },
    {
      id: 'l-sit-to-v-sit',
      name: 'L-Sit to V-Sit',
      aliases: ['l-sit to v-sit', 'l sit to v sit', 'v-sit progression'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: ['quads', 'shoulders'],
      equipment: ['parallettes'],
      requiresOneRM: false,
      description: 'Advanced core progression: lift from L-sit to V-sit position',
    },
    {
      id: 'dead-bug',
      name: 'Dead Bug',
      aliases: ['dead bug', 'dead bugs', 'deadbug'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
      description: 'Anti-extension core stability — opposite arm/leg reach from supine',
    },
    {
      id: 'mcgill-curl-up',
      name: 'McGill Curl-Up',
      aliases: ['mcgill curl up', 'mcgill curl-up', 'mcgill crunch', 'stuart mcgill curl up'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs'],
      secondaryMuscles: ['core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Spine-sparing ab exercise with isometric holds — hands under low back',
    },
    {
      id: 'hanging-knee-raise',
      name: 'Hanging Knee Raise',
      aliases: ['hanging knee raise', 'hanging knee raises', 'knee raise', 'hanging knee tuck'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['abs', 'core'],
      secondaryMuscles: ['forearms'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      description: 'Core exercise — raise knees toward chest while hanging',
    },
  ],

  // ============================================================================
  // CALISTHENICS - Advanced bodyweight progressions
  // ============================================================================
  calisthenics: [
    {
      id: 'scapular-push-ups',
      name: 'Scapular Push-Ups',
      aliases: ['scapular push ups', 'scapular push-ups', 'scap push ups', 'scap push-ups', 'shoulder blade push-ups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders', 'back'],
      secondaryMuscles: ['chest'],
      equipment: [],
      requiresOneRM: false,
      description: 'Push-up variation focusing on scapular protraction and retraction',
    },
    {
      id: 'pseudo-planche-push-ups',
      name: 'Pseudo Planche Push-Ups',
      aliases: ['pseudo planche push ups', 'pseudo planche push-ups', 'planche lean push ups', 'pppu'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest', 'shoulders'],
      secondaryMuscles: ['triceps', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Advanced push-up with hands positioned by hips, leaning forward',
    },
    {
      id: 'typewriter-pull-ups',
      name: 'Typewriter Pull-Ups',
      aliases: ['typewriter pull ups', 'typewriter pull-ups', 'archer pull ups', 'typewriters'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back', 'biceps'],
      secondaryMuscles: ['shoulders', 'core'],
      equipment: ['pull-up bar'],
      requiresOneRM: false,
      description: 'Advanced pull-up variation moving side to side at the top',
    },
    {
      id: 'hand-release-push-ups',
      name: 'Hand Release Push-Ups',
      aliases: ['hand release push ups', 'hand release push-ups', 'hrpu', 'hand release pushups'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['chest', 'triceps'],
      secondaryMuscles: ['shoulders', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Push-up where hands lift off floor at bottom - ensures full range of motion',
    },
    {
      id: 'air-squat',
      name: 'Air Squat',
      aliases: ['air squat', 'air squats', 'bodyweight squat', 'bodyweight squats', 'bw squat'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Basic bodyweight squat',
    },
    {
      id: 'shrimp-squat',
      name: 'Shrimp Squat',
      aliases: ['shrimp squat', 'shrimp squats'], // 'single leg squat' removed — it collided with Pistol Squats and dropped this entry in the merge
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Advanced single-leg squat holding rear foot behind',
    },
    {
      id: 'plank-to-down-dog',
      name: 'Plank to Down Dog',
      aliases: ['plank to down dog', 'plank to downward dog', 'down dog push up'],
      category: 'mobility',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['shoulders', 'core'],
      secondaryMuscles: ['hamstrings', 'back'],
      equipment: [],
      requiresOneRM: false,
      description: 'Flow movement between plank and downward dog positions',
    },
    {
      id: 'single-leg-bounds',
      name: 'Single-Leg Bounds',
      aliases: ['single leg bounds', 'single-leg bounds', 'bounding', 'power bounds'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['calves', 'core'],
      equipment: [],
      requiresOneRM: false,
      description: 'Plyometric single-leg jumping for power and explosiveness',
    },
    // 'glute-bridge' deleted 2026-07-16 — "Glute Bridge" aliases to free-db
    // "Butt Lift (Bridge)" (free-exercise-db's name for the glute bridge).
    {
      id: 'single-leg-glute-bridge',
      name: 'Single-Leg Glute Bridge',
      aliases: ['single leg glute bridge', 'single-leg bridge', 'one leg bridge'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['glutes'],
      secondaryMuscles: ['hamstrings', 'core'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'single-leg-romanian-deadlift',
      name: 'Single-Leg Romanian Deadlift',
      aliases: ['single leg rdl', 'single-leg rdl', 'one leg rdl', 'sl rdl'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['hamstrings', 'glutes'],
      secondaryMuscles: ['core', 'back'],
      equipment: ['dumbbells', 'kettlebell'],
      requiresOneRM: false,
    },
    {
      id: 'mountain-climbers',
      name: 'Mountain Climbers',
      aliases: ['mountain climbers', 'mountain climber'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['core', 'cardio-specific'],
      secondaryMuscles: ['shoulders', 'quads'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'jump-squats',
      name: 'Jump Squats',
      aliases: ['jump squats', 'jump squat', 'squat jumps'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['calves', 'core'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'jump-lunges',
      name: 'Jump Lunges',
      aliases: ['jump lunges', 'split jump', 'lunge jumps'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['calves', 'core'],
      equipment: [],
      requiresOneRM: false,
    },
    // Real exercise with no free-exercise-db equivalent. Before the 2026-07-16
    // debloat, "Jumping Jacks" fuzzy-resolved to Box Jump via its 'jump' alias
    // (wrong exercise); this entry makes it resolve correctly.
    {
      id: 'jumping-jacks',
      name: 'Jumping Jacks',
      aliases: ['jumping jacks', 'jumping jack', 'side straddle hop'],
      category: 'power',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['full-body', 'cardio-specific'],
      secondaryMuscles: ['calves', 'shoulders'],
      equipment: [],
      requiresOneRM: false,
      description: 'Rhythmic full-body warm-up — jump feet out while raising arms overhead, then return',
    },
    {
      id: 'cossack-squats',
      name: 'Cossack Squats',
      aliases: ['cossack squats', 'cossack squat', 'side lunge squat'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['quads', 'glutes'],
      secondaryMuscles: ['hamstrings'],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'russian-twist',
      name: 'Russian Twist',
      aliases: ['russian twist', 'russian twists', 'seated twist'],
      category: 'core',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['core'],
      secondaryMuscles: [],
      equipment: [],
      requiresOneRM: false,
    },
    {
      id: 'inverted-rows',
      name: 'Inverted Rows',
      aliases: ['inverted rows', 'inverted row', 'bodyweight row', 'australian pull-up'],
      category: 'strength',
      trackingType: 'bodyweight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps', 'core'],
      equipment: ['barbell', 'squat rack'],
      requiresOneRM: false,
    },
    {
      id: 'leg-press-calf-raise',
      name: 'Leg Press Calf Raise',
      aliases: ['leg press calf raise', 'calf press', 'leg press calf'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['calves'],
      secondaryMuscles: [],
      equipment: ['leg press'],
      requiresOneRM: false,
    },
    {
      id: 'triceps-pressdown',
      name: 'Triceps Pressdown',
      aliases: ['triceps pressdown', 'tricep pressdown', 'cable pressdown', 'pushdown'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['triceps'],
      secondaryMuscles: [],
      equipment: ['cable machine'],
      requiresOneRM: false,
    },
  ],

  // ============================================================================
  // ADDITIONAL STRENGTH - Missing exercises from programs
  // ============================================================================
  additionalStrength: [
    {
      id: 'incline-lat-pulldown',
      name: 'Incline Lat Pulldown',
      aliases: ['incline lat pulldown', 'incline pulldown', 'angled lat pulldown'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['back'],
      secondaryMuscles: ['biceps', 'shoulders'],
      equipment: ['cable machine'],
      requiresOneRM: false,
      description: 'Lat pulldown performed at an incline angle',
    },
    {
      id: 'cable-fly',
      name: 'Cable Fly',
      aliases: ['cable fly', 'cable flys', 'cable flyes', 'cable chest fly', 'standing cable fly'],
      category: 'strength',
      trackingType: 'weight-reps',
      primaryMuscles: ['chest'],
      secondaryMuscles: ['shoulders'],
      equipment: ['cable machine'],
      requiresOneRM: false,
      description: 'Chest isolation using cable crossover machine',
    },
  ],
};

/**
 * SMART DEDUPLICATION & SCHEMA PRIORITY
 * 
 * Strategy:
 * 1. Use FREE_EXERCISE_DB as primary source (their schema, categories, body parts)
 * 2. Overlay our enhancements (1RM tracking, requiresOneRM, custom metadata)
 * 3. Only add internal exercises that DON'T exist in FREE_EXERCISE_DB
 * 
 * This gives us:
 * - FREE_EXERCISE_DB's superior categorization and muscle targeting
 * - Our tactical/adaptive enhancements
 * - No duplicates
 */
function deduplicateAndMergeExercises(): Exercise[] {
  const exerciseMap = new Map<string, Exercise>();
  
  // Helper: Normalize name for comparison
  // Strips ALL non-alphanumeric including hyphens, apostrophes, and spaces
  // so "Band Pull-Apart" and "Band Pull Apart" produce the same key
  const normalizeName = (name: string) =>
    name.toLowerCase()
      .replace(/[^a-z0-9]/g, ''); // Strip everything except letters and numbers
  
  // Helper: Calculate similarity between two strings
  const calculateSimilarity = (str1: string, str2: string): number => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    if (longer.length === 0) return 1.0;
    return (longer.length - editDistance(longer, shorter)) / longer.length;
  };
  
  // Helper: Levenshtein distance
  const editDistance = (str1: string, str2: string): number => {
    const costs: number[] = [];
    for (let i = 0; i <= str2.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str1.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(j - 1) !== str2.charAt(i - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str1.length];
  };
  
  // Helper: Check if exercise exists by name or alias
  const exerciseExists = (exercise: Exercise): boolean => {
    const normalizedName = normalizeName(exercise.name);
    const existingExercises = Array.from(exerciseMap.values());
    
    // Keywords that make an exercise distinct (don't treat as duplicates)
    const distinctKeywords = ['bw', 'max reps', 'powerlifting', 'olympic', 'tactical', 'test', '1rm', '3rm'];
    const hasDistinctKeyword = (name: string) => {
      const lower = name.toLowerCase();
      return distinctKeywords.some(keyword => lower.includes(keyword));
    };
    
    // Check if this exact name exists
    for (const existing of existingExercises) {
      const existingNormalized = normalizeName(existing.name);
      
      // Exact match
      if (existingNormalized === normalizedName) return true;
      
      // If BOTH have distinct keywords, treat as different exercises
      if (hasDistinctKeyword(exercise.name) && hasDistinctKeyword(existing.name)) {
        continue; // Not a duplicate, check next
      }
      
      // High similarity match (>90% similar) - likely duplicate
      const similarity = calculateSimilarity(existingNormalized, normalizedName);
      if (similarity > 0.9) {
        console.log(` Skipping similar: "${exercise.name}" ~${(similarity * 100).toFixed(0)}% similar to "${existing.name}"`);
        return true;
      }
      
      // Check against aliases
      if (exercise.aliases.some(alias => 
        existing.aliases.some(existingAlias => 
          normalizeName(alias) === normalizeName(existingAlias)
        )
      )) return true;
      
      // Check if our name is in their aliases
      if (existing.aliases.some(alias => 
        normalizeName(alias) === normalizedName
      )) return true;
    }
    
    return false;
  };
  
  // STEP 1: Add ALL free-exercise-db exercises first (847 exercises)
  // These have the best categorization and muscle targeting
  console.log('� Loading FREE_EXERCISE_DB (847 exercises)...');
  FREE_EXERCISE_DB.forEach(exercise => {
    const key = normalizeName(exercise.name);
    exerciseMap.set(key, exercise);
  });
  
  // STEP 2: Add our internal exercises ONLY if they don't exist
  // This ensures we keep tactical/operator-specific exercises
  console.log('� Adding tactical-specific exercises from internal database...');
  const internalExercises = Object.values(EXERCISE_DATABASE).flat();
  let internalAdded = 0;
  
  internalExercises.forEach(exercise => {
    const key = normalizeName(exercise.name);

    // An internal exercise is only a genuine duplicate when something already
    // occupies ITS OWN name key. `exerciseExists` also returns true on a shared
    // ALIAS or a >90% name similarity — and when that happened to an exercise
    // whose name differs from the colliding entry, it was dropped here and then
    // never re-added by STEP 3 (which looks up by name key and finds nothing).
    // The exercise vanished from the merged DB entirely while still sitting in
    // the source file, so `findExercise` returned undefined for something that
    // is visibly "in the database".
    //
    // That is how Assault Bike disappeared: its 'air bike' alias collides with
    // free-exercise-db's "Air Bike" — which is a BICYCLE CRUNCH, a completely
    // different movement — so the tactical cardio entry was erased. Taskforce
    // Spearhead's Grinder Circuit prescribes it by name, and it resolved to
    // nothing. Same mechanism previously erased Shrimp Squat (see its alias
    // comment) and Air Squat.
    //
    // Exact-name duplicates are still deduped: the key check below is the real
    // test, and STEP 3 overlays our tactical metadata onto the free-db entry
    // that owns the name.
    if (!exerciseMap.has(key)) {
      exerciseMap.set(key, exercise);
      internalAdded++;
    } else {
      console.log(` Skipping duplicate: ${exercise.name}`);
    }
  });
  
  // STEP 3: Apply internal DB overrides — tracking types, canonical names, and tactical metadata.
  // FREE-EXERCISE-DB remains authoritative for images, instructions, and descriptions.
  // Internal DB overrides ONLY: trackingType, aliases, and adds exercises that don't exist.
  console.log(' Applying internal DB overrides (tracking types + tactical exercises)...');
  let overrideCount = 0;

  // Build a quick lookup for free-exercise-db by normalized name (for image bridging)
  const freeDbByName = new Map<string, Exercise>();
  FREE_EXERCISE_DB.forEach(ex => freeDbByName.set(normalizeName(ex.name), ex));

  internalExercises.forEach(exercise => {
    const key = normalizeName(exercise.name);
    const existing = exerciseMap.get(key);

    if (existing) {
      // Exercise exists in free-exercise-db — overlay our tactical metadata
      // but PRESERVE free-exercise-db images, instructions, descriptions
      const merged: Exercise = {
        ...existing,                                         // Free-exercise-db base (images, instructions, descriptions)
        category: exercise.category,                         // Our category is authoritative — free-DB doesn't model
                                                             // carry/ruck/tactical distinctions; we do.
        trackingType: exercise.trackingType,                 // Our tracking type is authoritative
        aliases: Array.from(new Set([...existing.aliases, ...exercise.aliases])), // Merge aliases
        requiresOneRM: exercise.requiresOneRM,               // Our 1RM setting is authoritative
        // Only override these if our internal entry explicitly has them
        ...(exercise.force && { force: exercise.force }),
        ...(exercise.mechanic && { mechanic: exercise.mechanic }),
        ...(exercise.level && { level: exercise.level }),
        ...(exercise.tags && { tags: exercise.tags }),
        ...(exercise.variations && { variations: exercise.variations }),
        // Only override descriptions/instructions if we have BETTER ones (non-empty)
        ...(exercise.description && { description: exercise.description }),
        ...(exercise.instructions && exercise.instructions.length > 0 && { instructions: exercise.instructions }),
      };

      if (existing.trackingType !== exercise.trackingType) {
        console.log(` Override tracking: "${exercise.name}": ${existing.trackingType} → ${exercise.trackingType}`);
        overrideCount++;
      }
      if (existing.category !== exercise.category) {
        console.log(` Override category: "${exercise.name}": ${existing.category} → ${exercise.category}`);
        overrideCount++;
      }
      exerciseMap.set(key, merged);
    }
    // If exercise doesn't exist in free-exercise-db, it was already added in Step 2
  });

  // STEP 4: Image Bridge — pull images from free-exercise-db for internal exercises
  // that don't have images yet. Uses IMAGE_BRIDGE_MAP to find the closest match.
  let imageBridgeCount = 0;
  for (const [internalName, freeDbName] of Object.entries(IMAGE_BRIDGE_MAP)) {
    const internalKey = normalizeName(internalName);
    const internalExercise = exerciseMap.get(internalKey);
    if (!internalExercise) continue;

    // Already has images from a direct match — skip
    if (internalExercise.images && internalExercise.images.length > 0) continue;

    // Find the free-exercise-db entry by name
    const freeDbKey = normalizeName(freeDbName);
    const freeDbEntry = freeDbByName.get(freeDbKey);
    if (freeDbEntry?.images && freeDbEntry.images.length > 0) {
      internalExercise.images = freeDbEntry.images;
      // Also pull instructions/description if the internal exercise doesn't have them
      if (!internalExercise.instructions || internalExercise.instructions.length === 0) {
        internalExercise.instructions = freeDbEntry.instructions;
      }
      if (!internalExercise.description) {
        internalExercise.description = freeDbEntry.description;
      }
      exerciseMap.set(internalKey, internalExercise);
      imageBridgeCount++;
    }
  }
  
  // STEP 5: Apply audited overrides from UNILATERAL_OVERRIDES + TRACKING_TYPE_AUDIT_OVERRIDES.
  // Single O(n) pass over the merged map; each entry checks its id against
  // the two override tables. This is how we pin classifications that the
  // runtime regex heuristic gets wrong (Olympic lifts caught by "clean" /
  // "snatch" patterns, Two-DB rows the user wants per-side, etc.) without
  // having to maintain an internal entry just to override metadata.
  let unilateralOverrideCount = 0;
  let trackingAuditOverrideCount = 0;
  let descriptionCount = 0;
  exerciseMap.forEach(ex => {
    if (Object.prototype.hasOwnProperty.call(UNILATERAL_OVERRIDES, ex.id)) {
      const desired = UNILATERAL_OVERRIDES[ex.id];
      if (ex.unilateral !== desired) {
        ex.unilateral = desired;
        unilateralOverrideCount++;
      }
    }
    if (Object.prototype.hasOwnProperty.call(TRACKING_TYPE_AUDIT_OVERRIDES, ex.id)) {
      const desired = TRACKING_TYPE_AUDIT_OVERRIDES[ex.id]!;
      if (ex.trackingType !== desired) {
        ex.trackingType = desired;
        trackingAuditOverrideCount++;
      }
    }
    if (Object.prototype.hasOwnProperty.call(LOAD_BASIS_AUDIT_OVERRIDES, ex.id)) {
      const desired = LOAD_BASIS_AUDIT_OVERRIDES[ex.id]!;
      if (ex.loadBasis !== desired) { ex.loadBasis = desired; }
    }
    // Audited descriptions — the map holds only the manually-audited ids, so
    // these authored versions replace any terse pre-existing text.
    if (EXERCISE_DESCRIPTIONS[ex.id]) {
      ex.description = EXERCISE_DESCRIPTIONS[ex.id];
      descriptionCount++;
    }
  });

  console.log(` Deduplicated database: ${exerciseMap.size} total exercises`);
  console.log(`   - ${FREE_EXERCISE_DB.length} from free-exercise-db`);
  console.log(`   - ${internalAdded} tactical-specific from internal database`);
  console.log(`   - ${internalExercises.length - internalAdded} duplicates removed`);
  console.log(`   - ${overrideCount} tracking type overrides applied`);
  console.log(`   - ${imageBridgeCount} image bridges applied`);
  console.log(`   - ${unilateralOverrideCount} audited unilateral overrides applied`);
  console.log(`   - ${trackingAuditOverrideCount} audited tracking type overrides applied`);
  console.log(`   - ${descriptionCount} audited descriptions applied`);

  return Array.from(exerciseMap.values());
}

// Flatten all exercises into a single searchable array WITH DEDUPLICATION
// FREE_EXERCISE_DB takes priority for schema/categorization
export const ALL_EXERCISES: Exercise[] = deduplicateAndMergeExercises();

/**
 * Find an exercise by name (fuzzy match)
 */
export function findExercise(query: string): Exercise | undefined {
  if (!query) return undefined;
  
  // First, check if this is a mapped alias (for backwards compatibility)
  const resolvedName = resolveExerciseName(query);
  const searchTerm = resolvedName.toLowerCase().trim();
  
  // Try exact match on name (using resolved name)
  let match = ALL_EXERCISES.find(ex => ex.name.toLowerCase() === searchTerm);
  if (match) return match;
  
  // Also try with original query in case resolved didn't help
  const originalTerm = query.toLowerCase().trim();
  if (originalTerm !== searchTerm) {
    match = ALL_EXERCISES.find(ex => ex.name.toLowerCase() === originalTerm);
    if (match) return match;
  }
  
  // Try exact match on aliases
  match = ALL_EXERCISES.find(ex => 
    ex.aliases.some(alias => alias.toLowerCase() === searchTerm || alias.toLowerCase() === originalTerm)
  );
  if (match) return match;
  
  // Try partial match on name
  match = ALL_EXERCISES.find(ex => 
    ex.name.toLowerCase().includes(searchTerm) || searchTerm.includes(ex.name.toLowerCase())
  );
  if (match) return match;
  
  // Try partial match on aliases
  match = ALL_EXERCISES.find(ex => 
    ex.aliases.some(alias => 
      alias.toLowerCase().includes(searchTerm) || searchTerm.includes(alias.toLowerCase())
    )
  );
  if (match) return match;
  
  return undefined;
}

/**
 * Get tracking type for an exercise
 */
export function getTrackingType(exerciseName: string): TrackingType | undefined {
  const exercise = findExercise(exerciseName);
  return exercise?.trackingType;
}

/**
 * Get muscle groups worked by an exercise
 */
export function getMuscleGroups(exerciseName: string): { primary: MuscleGroup[], secondary: MuscleGroup[] } {
  const exercise = findExercise(exerciseName);
  return {
    primary: exercise?.primaryMuscles || [],
    secondary: exercise?.secondaryMuscles || []
  };
}

/**
 * Search exercises by category
 */
export function getExercisesByCategory(category: ExerciseCategory): Exercise[] {
  return EXERCISE_DATABASE[category] || [];
}

/**
 * Search exercises by muscle group
 */
export function getExercisesByMuscle(muscleGroup: MuscleGroup): Exercise[] {
  return ALL_EXERCISES.filter(ex => 
    ex.primaryMuscles.includes(muscleGroup) || ex.secondaryMuscles.includes(muscleGroup)
  );
}

/**
 * Get exercises that require 1RM
 */
export function getOneRMExercises(): Exercise[] {
  return ALL_EXERCISES.filter(ex => ex.requiresOneRM);
}

/**
 * ADAPTIVE SYSTEM HELPERS
 * Enhanced functions for metadata-aware adaptive recommendations
 */

/**
 * Find exercise by ID (more reliable than name matching)
 */
export function findExerciseById(id: string): Exercise | undefined {
  return ALL_EXERCISES.find(ex => ex.id === id);
}

/**
 * Is this exercise inherently unilateral (one side at a time)?
 *
 *  Infers from the exercise record: name pattern, aliases, description,
 *  variations. Avoids needing to manually flag every record in the
 *  database — the signal already exists in those fields, we just
 *  consolidate the check here.
 *
 *  Returns true for:
 *    Bulgarian split squat, pistol/skater/cossack/shrimp squats,
 *    single-arm / single-leg / one-arm / one-leg variants (DB rows,
 *    cable rows, landmine press, lateral raises, etc.), step-ups,
 *    lunges, hip airplanes, suitcase carries, anti-lateral-flexion
 *    work, and anything whose name or aliases explicitly contain
 *    "unilateral".
 *
 *  Used by V2's adapt() to auto-toggle the per-side display + ML
 *  volume doubling. Falls back to a name-only regex when the
 *  database has no entry for the prescribed exercise (program
 *  variants the DB doesn't yet know about).
 */
export function isUnilateralExercise(exerciseOrName: Exercise | string | undefined): boolean {
  if (!exerciseOrName) return false;
  // Resolve to an Exercise record if we got a name string.
  const ex = typeof exerciseOrName === 'string'
    ? findExercise(exerciseOrName)
    : exerciseOrName;

  // Explicit override wins — the database author knows better than the
  // name/variation regex (which e.g. trips on RDL's "Single Leg" variation).
  if (ex && typeof ex.unilateral === 'boolean') return ex.unilateral;

  // Guard against FUZZY resolution. findExercise() falls through to a partial
  // name match, so `findExercise('Cable Crunch')` can resolve to a longer,
  // unrelated variant like "Bosu Ball Cable Crunch With Side Bends" whose
  // description/variation text ("each side", "side bends") then trips the
  // per-side regex below — flagging a plain bilateral movement as unilateral.
  // Only trust a resolved entry's EXTENDED corpus when it actually matches the
  // query by exact name or alias; otherwise infer from the query name alone.
  // (Reported 2026-07-02: "Cable crunch is not per side.")
  const query = typeof exerciseOrName === 'string' ? exerciseOrName.toLowerCase().trim() : null;
  const isExactHit = !!ex && (
    query === null ||
    ex.name.toLowerCase() === query ||
    (ex.aliases ?? []).some(a => a.toLowerCase() === query)
  );

  // Compose the corpus we'll regex against: name + aliases + description
  // + variations. Anything in any of those that screams "per side" wins.
  const haystack: string[] = [];
  if (ex && isExactHit) {
    haystack.push(ex.name.toLowerCase());
    for (const a of ex.aliases ?? []) haystack.push(a.toLowerCase());
    if (ex.description) haystack.push(ex.description.toLowerCase());
    for (const v of ex.variations ?? []) haystack.push(v.toLowerCase());
  } else if (query !== null) {
    // No DB hit, or only a fuzzy one — fall back to name-only inference on the
    // ORIGINAL query. Same heuristic V2 uses; centralized here so other
    // consumers (rec engine, analytics) can call this without duplicating it.
    haystack.push(query);
  }

  for (const text of haystack) {
    if (/\b(single[-\s]?arm|single[-\s]?leg|one[-\s]?arm|one[-\s]?leg|unilateral)\b/.test(text)) return true;
    if (/bulgarian\s+split|pistol\s+squat|skater\s+squat|cossack\s+squat|shrimp\s+squat/.test(text)) return true;
    if (/\bsplit\s+squat\b|\bstep[-\s]?up\b/.test(text)) return true;
    // Suitcase carry / hip airplane — explicitly per-side movements
    if (/suitcase\s+carry|hip\s+airplane/.test(text)) return true;
    // Generic "per side" / "each side" / "alternating" descriptors
    if (/\bper\s+side\b|\beach\s+side\b|\beach\s+leg\b|\beach\s+arm\b/.test(text)) return true;
  }
  return false;
}

/**
 * Whether an exercise's logged weight is PER HAND (two dumbbells, one per hand,
 * moving together) → its true volume is weight × reps × 2. Single source of
 * truth for the "×2 dumbbell" volume correction and the "ea" weight label.
 *
 * Guard: unilateral movements always return false here — their doubling is
 * already handled by the per-side reps path (reps L+R), so applying per-hand
 * ×2 on top would 4× them. This makes the two mechanisms mutually exclusive
 * and safe even when an exercise is mis-classified.
 */
export function isPerHandLoad(exerciseOrName: Exercise | string | undefined): boolean {
  if (!exerciseOrName) return false;
  const ex = typeof exerciseOrName === 'string' ? findExercise(exerciseOrName) : exerciseOrName;
  const name = (typeof exerciseOrName === 'string' ? exerciseOrName : ex?.name ?? '').toLowerCase();

  // Per-side doubling owns this movement — don't stack per-hand on top.
  if (isUnilateralExercise(ex ?? (typeof exerciseOrName === 'string' ? exerciseOrName : undefined))) return false;

  // Explicit audit decision wins.
  if (ex?.loadBasis === 'per-hand') return true;
  if (ex?.loadBasis === 'total') return false;

  // Infer: needs two dumbbells. Exclude single-implement holds (goblet /
  // landmine / one KB), barbell / machine / cable, and single-arm variants.
  const equip = (ex?.equipment ?? []).map(e => e.toLowerCase());
  const hasDumbbell = /dumbbell|\bdb\b/.test(name) || equip.some(e => e.includes('dumbbell'));
  if (!hasDumbbell) return false;
  if (/goblet|landmine|barbell|\bbb\b|cable|machine|smith|trap bar|hex bar|sandbag|\bplate\b|kettlebell|\bkb\b|swing|single[-\s]?arm|one[-\s]?arm|1[-\s]?arm|\bsa\b|alternating/.test(name)) return false;
  return true;
}

/**
 * Get comprehensive exercise metadata for adaptive system
 */
export interface ExerciseMetadata {
  id: string;
  name: string;
  level: 'beginner' | 'intermediate' | 'expert' | null;
  mechanic: 'compound' | 'isolation' | null;
  force: 'push' | 'pull' | 'static' | null;
  category: ExerciseCategory;
  requiresOneRM: boolean;
  trackingType: TrackingType;
  primaryMuscles: MuscleGroup[];
}

export function getExerciseMetadata(exerciseNameOrId: string): ExerciseMetadata | null {
  // Try ID first, then name
  let exercise = findExerciseById(exerciseNameOrId);
  if (!exercise) {
    exercise = findExercise(exerciseNameOrId);
  }
  
  if (!exercise) return null;
  
  return {
    id: exercise.id,
    name: exercise.name,
    level: exercise.level || null,
    mechanic: exercise.mechanic || null,
    force: exercise.force || null,
    category: exercise.category,
    requiresOneRM: exercise.requiresOneRM,
    trackingType: exercise.trackingType,
    primaryMuscles: exercise.primaryMuscles,
  };
}

/**
 * Check if exercise is a compound movement (uses metadata first, fallback to heuristic)
 */
export function isCompoundMovement(exerciseNameOrId: string): boolean {
  const metadata = getExerciseMetadata(exerciseNameOrId);
  
  // Use metadata if available
  if (metadata?.mechanic === 'compound') return true;
  if (metadata?.mechanic === 'isolation') return false;
  
  // Fallback to heuristic (for exercises without metadata)
  const compounds = [
    'squat', 'deadlift', 'bench', 'press', 'row', 'pull-up', 'chin-up',
    'dip', 'lunge', 'clean', 'snatch', 'jerk', 'thruster'
  ];
  const nameLower = exerciseNameOrId.toLowerCase();
  return compounds.some(compound => nameLower.includes(compound));
}

/**
 * Get exercise difficulty level (uses metadata first, fallback to heuristic)
 */
export function getExerciseLevel(exerciseNameOrId: string): 'beginner' | 'intermediate' | 'expert' {
  const metadata = getExerciseMetadata(exerciseNameOrId);
  
  // Use metadata if available
  if (metadata?.level) return metadata.level;
  
  // Fallback heuristic based on exercise type
  const nameLower = exerciseNameOrId.toLowerCase();
  
  // Expert level exercises
  if (nameLower.includes('olympic') || 
      nameLower.includes('snatch') || 
      nameLower.includes('clean') ||
      nameLower.includes('jerk') ||
      nameLower.includes('muscle-up')) {
    return 'expert';
  }
  
  // Beginner level exercises
  if (nameLower.includes('machine') ||
      nameLower.includes('assisted') ||
      nameLower.includes('cable') ||
      nameLower.includes('band')) {
    return 'beginner';
  }
  
  // Default to intermediate
  return 'intermediate';
}

/**
 * Get force type for exercise (push/pull/static)
 */
export function getForceType(exerciseNameOrId: string): 'push' | 'pull' | 'static' | null {
  const metadata = getExerciseMetadata(exerciseNameOrId);
  return metadata?.force || null;
}

/**
 * EXERCISE FAMILIES
 *
 * Groups of exercises that work the same movement pattern.
 * Used for fallback recommendations when specific exercise history is unavailable.
 */
const EXERCISE_FAMILIES: Record<string, string[]> = {
  // Bench Press Family
  'bench-press': [
    'bench press', 'bench', 'barbell bench', 'dumbbell bench', 'incline bench',
    'decline bench', 'close grip bench', 'wide grip bench', 'floor press',
    'smith machine bench', 'machine chest press'
  ],

  // Squat Family
  'squat': [
    'squat', 'back squat', 'front squat', 'goblet squat', 'box squat',
    'pause squat', 'hack squat', 'bulgarian split squat', 'split squat',
    'smith squat', 'zercher squat', 'safety bar squat'
  ],

  // Deadlift Family
  'deadlift': [
    'deadlift', 'conventional deadlift', 'sumo deadlift', 'romanian deadlift', 'rdl',
    'stiff leg deadlift', 'deficit deadlift', 'block pull', 'rack pull',
    'trap bar deadlift', 'hex bar deadlift'
  ],

  // Row Family
  'row': [
    'row', 'barbell row', 'dumbbell row', 'bent over row', 'pendlay row',
    'cable row', 'seated row', 't-bar row', 'chest supported row',
    'meadows row', 'one arm row', 'single arm row', 'kroc row'
  ],

  // Overhead Press Family
  'overhead-press': [
    'overhead press', 'ohp', 'military press', 'strict press', 'push press',
    'dumbbell shoulder press', 'seated press', 'arnold press', 'z press',
    'landmine press', 'viking press'
  ],

  // Pull-up Family
  'pull-up': [
    'pull-up', 'pullup', 'pull up', 'chin-up', 'chinup', 'chin up',
    'lat pulldown', 'assisted pull-up', 'weighted pull-up', 'muscle-up',
    'neutral grip pull-up', 'wide grip pull-up', 'close grip pull-up'
  ],

  // Curl Family
  'curl': [
    'curl', 'bicep curl', 'barbell curl', 'dumbbell curl', 'hammer curl',
    'preacher curl', 'incline curl', 'cable curl', 'ez bar curl',
    'spider curl', 'concentration curl', 'reverse curl'
  ],

  // Tricep Family
  'tricep': [
    'tricep', 'triceps', 'pushdown', 'pressdown', 'skull crusher',
    'overhead extension', 'tricep extension', 'dip', 'close grip bench',
    'diamond push-up', 'kickback'
  ],

  // Lateral Raise Family
  'lateral-raise': [
    'lateral raise', 'side raise', 'lateral', 'dumbbell lateral',
    'cable lateral', 'machine lateral', 'leaning lateral'
  ],

  // Leg Curl Family
  'leg-curl': [
    'leg curl', 'hamstring curl', 'lying leg curl', 'seated leg curl',
    'nordic curl', 'stability ball leg curl', 'glute ham raise', 'ghr'
  ],

  // Leg Extension Family
  'leg-extension': [
    'leg extension', 'quad extension', 'machine leg extension',
    'single leg extension'
  ],

  // Lunge Family
  'lunge': [
    'lunge', 'lunges', 'walking lunge', 'reverse lunge', 'forward lunge',
    'lateral lunge', 'step up', 'step-up', 'bulgarian', 'split squat'
  ],

  // Hip Thrust Family
  'hip-thrust': [
    'hip thrust', 'glute bridge', 'barbell hip thrust', 'single leg hip thrust',
    'banded hip thrust', 'frog pump'
  ],

  // Calf Raise Family
  'calf-raise': [
    'calf raise', 'calf', 'standing calf', 'seated calf', 'donkey calf',
    'leg press calf raise', 'single leg calf'
  ],

  // Shrug Family
  'shrug': [
    'shrug', 'barbell shrug', 'dumbbell shrug', 'trap shrug',
    'farmer carry', 'farmers walk'
  ],

  // Face Pull Family
  'face-pull': [
    'face pull', 'rear delt', 'reverse fly', 'band pull apart',
    'rear delt fly', 'cable face pull'
  ],

  // Fly Family
  'fly': [
    'fly', 'flye', 'chest fly', 'dumbbell fly', 'cable fly',
    'pec deck', 'machine fly', 'incline fly'
  ],

  // Push-up Family
  'push-up': [
    'push-up', 'pushup', 'push up', 'diamond push-up', 'wide push-up',
    'decline push-up', 'incline push-up', 'pike push-up', 'explosive push-up'
  ],

  // Kettlebell Swing Family
  'kettlebell-swing': [
    'kettlebell swing', 'kb swing', 'russian swing', 'american swing',
    'single arm swing', 'double swing'
  ],

  // Ruck Family
  'ruck': [
    'ruck', 'ruck march', 'rucking', 'weighted march', 'ruck walk',
    'ruck run'
  ],

  // Run Family
  'run': [
    'run', 'running', 'jog', 'sprint', 'interval run', 'tempo run',
    'long run', 'easy run'
  ]
};

/**
 * Get exercise family tags for an exercise
 * Returns array of family names this exercise belongs to
 */
export function getExerciseFamily(exerciseNameOrId: string): string[] {
  const exercise = findExercise(exerciseNameOrId);
  const nameToCheck = (exercise?.name || exerciseNameOrId).toLowerCase();

  const families: string[] = [];

  for (const [familyName, keywords] of Object.entries(EXERCISE_FAMILIES)) {
    for (const keyword of keywords) {
      if (nameToCheck.includes(keyword) || keyword.includes(nameToCheck)) {
        families.push(familyName);
        break; // Only add each family once
      }
    }
  }

  return families;
}

/**
 * Find exercises in the same family as a given exercise
 */
export function getRelatedExercises(exerciseNameOrId: string, limit: number = 5): Exercise[] {
  const families = getExerciseFamily(exerciseNameOrId);
  if (families.length === 0) return [];

  const related: Exercise[] = [];
  const sourceExercise = findExercise(exerciseNameOrId);
  const sourceName = sourceExercise?.name?.toLowerCase() || exerciseNameOrId.toLowerCase();

  // Search all exercises for family matches
  for (const ex of ALL_EXERCISES) {
    if (related.length >= limit) break;
    if (ex.name.toLowerCase() === sourceName) continue; // Skip source exercise

    const exFamilies = getExerciseFamily(ex.name);
    if (exFamilies.some(f => families.includes(f))) {
      related.push(ex);
    }
  }

  return related;
}

// ============================================================================
// SCORED SEARCH
// ============================================================================

export interface SearchResult {
  exercise: Exercise;
  score: number;
}

export interface SearchOptions {
  category?: ExerciseCategory;
  limit?: number;
}

/**
 * Scored fuzzy search across all exercise fields.
 * Multi-word queries are tokenized and scored independently (summed).
 * An exercise must match at least one token to appear in results.
 * Supports optional category filter (AND logic with search).
 */
export function searchExercises(query: string, options?: SearchOptions): SearchResult[] {
  const { category, limit = 30 } = options || {};
  const trimmed = query.trim().toLowerCase();

  // Determine the pool (apply category filter first for efficiency)
  let pool = ALL_EXERCISES;
  if (category) {
    pool = pool.filter(ex => ex.category === category);
  }

  if (!trimmed) {
    // No query — return pool in existing order, capped
    return pool.slice(0, limit).map(ex => ({ exercise: ex, score: 0 }));
  }

  const tokens = trimmed.split(/\s+/).filter(Boolean);

  // Resolve the WHOLE query through our canonicalization so users find a lift by
  // a name they know differently — "back squat" → Barbell Squat, "incline db
  // press" → Incline Dumbbell Press. Computed once, applied as a strong boost.
  const resolvedQuery = resolveExerciseName(trimmed).toLowerCase();
  const queryKey = normalizeExerciseMatchKey(trimmed);

  const results: SearchResult[] = [];

  for (const ex of pool) {
    let totalScore = 0;
    let anyTokenMatched = false;
    const nameLower = ex.name.toLowerCase();

    for (const token of tokens) {
      let tokenScore = 0;

      // Name scoring
      if (nameLower === token) {
        tokenScore = Math.max(tokenScore, 100);
      } else if (nameLower.startsWith(token)) {
        tokenScore = Math.max(tokenScore, 80);
      } else if (nameLower.includes(token)) {
        tokenScore = Math.max(tokenScore, 50);
      }

      // Alias scoring
      for (const alias of ex.aliases) {
        const aliasLower = alias.toLowerCase();
        if (aliasLower === token) {
          tokenScore = Math.max(tokenScore, 90);
        } else if (aliasLower.includes(token)) {
          tokenScore = Math.max(tokenScore, 40);
        }
      }

      // Primary muscle match
      for (const m of ex.primaryMuscles) {
        if (m.toLowerCase().includes(token) || token.includes(m.toLowerCase())) {
          tokenScore = Math.max(tokenScore, 35);
        }
      }

      // Tag match
      if (ex.tags) {
        for (const t of ex.tags) {
          if (t.toLowerCase().includes(token) || token.includes(t.toLowerCase())) {
            tokenScore = Math.max(tokenScore, 30);
          }
        }
      }

      // Equipment match
      if (ex.equipment) {
        for (const e of ex.equipment) {
          if (e.toLowerCase().includes(token) || token.includes(e.toLowerCase())) {
            tokenScore = Math.max(tokenScore, 25);
          }
        }
      }

      // Category match
      if (ex.category.toLowerCase().includes(token)) {
        tokenScore = Math.max(tokenScore, 20);
      }

      // Secondary muscle match
      for (const m of ex.secondaryMuscles) {
        if (m.toLowerCase().includes(token) || token.includes(m.toLowerCase())) {
          tokenScore = Math.max(tokenScore, 15);
        }
      }

      if (tokenScore > 0) anyTokenMatched = true;
      totalScore += tokenScore;
    }

    // Whole-query canonical/alias match — collapses spelling variants the
    // per-token pass misses (DB↔Dumbbell, plural, hyphen) and routes known
    // synonyms via the alias map.
    if (
      nameLower === resolvedQuery ||
      normalizeExerciseMatchKey(ex.name) === queryKey ||
      ex.aliases.some(a => normalizeExerciseMatchKey(a) === queryKey)
    ) {
      totalScore = Math.max(totalScore, 95);
      anyTokenMatched = true;
    }

    if (anyTokenMatched) {
      results.push({ exercise: ex, score: totalScore });
    }
  }

  // Sort by score descending, then alphabetically for ties
  results.sort((a, b) => b.score - a.score || a.exercise.name.localeCompare(b.exercise.name));

  return results.slice(0, limit);
}
